import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";

import maplibregl from "maplibre-gl";
import pickupPinImage from "../../assets/cargaazul_optimized_92.webp";
import deliveryPinImage from "../../assets/descargavermelha_optimized_92.webp";
import { addSharedOperationalRouteLayers } from "./sharedOperationalRouteLayers";
import { OperationalSessionTabs, OperationalSimpleStop } from "./OperationalRoutePanelParts";

import {
  ensureRoadReferenceOverlay,
} from "./roadReferenceOverlay";


import {
  AlertCircle,
  ChevronLeft,
  ChevronRight,
  Clock3,
  MapPin,
  RefreshCw,
  Route,
  Search,
  Truck,
  User,
} from "lucide-react";

import {
  useTranslation,
} from "react-i18next";

import api from "../../axiosConfig";
import {
  getSocket,
} from "../../lib/socket";

import "maplibre-gl/dist/maplibre-gl.css";
import "../../styles/MapBase.css";
import "../../styles/LiveTransportsMap.css";

const MAP_STYLE =
  "/maps/styles/loadevo-road.json?v=5";

const DEFAULT_CENTER = [
  -8.61099,
  41.14961,
];

const DEFAULT_ZOOM =
  5;

const SESSION_ROUTES_SOURCE_ID =
  "driver-route-planner-session-routes";

const SESSION_ROUTES_CASING_LAYER_ID =
  "driver-route-planner-session-routes-casing";

const SESSION_ROUTES_LINE_LAYER_ID =
  "driver-route-planner-session-routes-line";

const SESSION_ROUTES_HISTORICAL_LAYER_ID =
  "driver-route-planner-session-routes-historical";

const SESSION_ROUTES_HITBOX_LAYER_ID =
  "driver-route-planner-session-routes-hitbox";


const ROUTES_SOURCE_ID =
  "driver-route-planner-routes";


const ROUTES_CASING_LAYER_ID =
  "driver-route-planner-routes-casing";

const ROUTES_LINE_LAYER_ID =
  "driver-route-planner-routes-line";

const ROUTES_HITBOX_LAYER_ID =
  "driver-route-planner-routes-hitbox";

const POINTS_SOURCE_ID =
  "driver-route-planner-points";

const POINTS_HALO_LAYER_ID =
  "driver-route-planner-points-halo";

const POINTS_CIRCLE_LAYER_ID =
  "driver-route-planner-points-circle";

const POINTS_LABEL_LAYER_ID =
  "driver-route-planner-points-label";

const CACHE_MAX_AGE_MS =
  12 *
  60 *
  60 *
  1000;

function getStoredToken() {
  return (
    window.localStorage.getItem(
      "token"
    ) ||
    window.localStorage.getItem(
      "authToken"
    ) ||
    window.sessionStorage.getItem(
      "token"
    ) ||
    window.sessionStorage.getItem(
      "authToken"
    ) ||
    ""
  );
}

function decodeTokenPayload(
  token
) {
  try {
    const encoded =
      token
        .split(".")[1]
        .replace(
          /-/g,
          "+"
        )
        .replace(
          /_/g,
          "/"
        );

    return JSON.parse(
      window.atob(
        encoded
      )
    );
  } catch {
    return null;
  }
}

function resolveCompanyScope() {
  const token =
    getStoredToken();

  const payload =
    token
      ? decodeTokenPayload(
          token
        )
      : null;

  const companyId =
    Number(
      payload?.company_id ??
      payload?.companyId
    );

  if (
    Number.isInteger(
      companyId
    ) &&
    companyId > 0
  ) {
    return `company:${companyId}`;
  }

  const directCompanyId =
    Number(
      window.sessionStorage.getItem(
        "companyId"
      ) ||
      window.sessionStorage.getItem(
        "company_id"
      ) ||
      window.localStorage.getItem(
        "companyId"
      ) ||
      window.localStorage.getItem(
        "company_id"
      )
    );

  if (
    Number.isInteger(
      directCompanyId
    ) &&
    directCompanyId > 0
  ) {
    return `company:${directCompanyId}`;
  }

  if (token) {
    return `token:${token.slice(-24)}`;
  }

  return null;
}

function getPlannerCacheKey() {
  const scope =
    resolveCompanyScope();

  if (!scope) {
    return null;
  }

  return `loadevo.map.driver-route-planner.v2.${scope}`;
}

function readPlannerCache() {
  const cacheKey =
    getPlannerCacheKey();

  if (!cacheKey) {
    return null;
  }

  try {
    const raw =
      window.sessionStorage.getItem(
        cacheKey
      );

    if (!raw) {
      return null;
    }

    const parsed =
      JSON.parse(raw);

    const savedAt =
      Number(
        parsed?.savedAt
      ) ||
      0;

    if (
      !savedAt ||
      Date.now() -
        savedAt >
        CACHE_MAX_AGE_MS
    ) {
      window.sessionStorage.removeItem(
        cacheKey
      );

      return null;
    }

    if (
      !parsed?.payload ||
      !Array.isArray(
        parsed.payload
          .drivers
      )
    ) {
      return null;
    }

    return parsed.payload;
  } catch {
    return null;
  }
}

function writePlannerCache(
  payload
) {
  const cacheKey =
    getPlannerCacheKey();

  if (
    !cacheKey ||
    !payload
  ) {
    return;
  }

  try {
    window.sessionStorage.setItem(
      cacheKey,
      JSON.stringify({
        savedAt:
          Date.now(),

        payload,
      })
    );
  } catch (
    error
  ) {
    console.warn(
      "[DriverRoutePlannerMap] cache write failed:",
      error?.message
    );
  }
}

function formatDuration(
  seconds
) {
  const value =
    Number(seconds);

  if (
    !Number.isFinite(
      value
    )
  ) {
    return null;
  }

  const negative =
    value < 0;

  const totalMinutes =
    Math.round(
      Math.abs(value) /
        60
    );

  const hours =
    Math.floor(
      totalMinutes /
        60
    );

  const minutes =
    totalMinutes %
    60;

  let label =
    "";

  if (
    hours <= 0
  ) {
    label =
      `${minutes} min`;
  } else if (
    minutes <= 0
  ) {
    label =
      `${hours} h`;
  } else {
    label =
      `${hours} h ${minutes} min`;
  }

  return negative
    ? `-${label}`
    : label;
}

function formatKm(
  value
) {
  const number =
    Number(value);

  if (
    !Number.isFinite(
      number
    )
  ) {
    return null;
  }

  if (number === 0) {
    return "0 km";
  }

  return `${number
    .toFixed(
      number >= 100
        ? 0
        : 1
    )
    .replace(
      ".",
      ","
    )} km`;
}

function formatUpdatedAt(
  value,
  locale
) {
  if (!value) {
    return null;
  }

  const date =
    new Date(value);

  if (
    Number.isNaN(
      date.getTime()
    )
  ) {
    return null;
  }

  return new Intl.DateTimeFormat(
    locale,
    {
      hour:
        "2-digit",

      minute:
        "2-digit",
    }
  ).format(
    date
  );
}

function formatDateTime(
  value,
  locale
) {
  if (!value) {
    return null;
  }

  const date =
    new Date(value);

  if (
    Number.isNaN(
      date.getTime()
    )
  ) {
    return null;
  }

  return new Intl.DateTimeFormat(
    locale,
    {
      day:
        "2-digit",

      month:
        "2-digit",

      hour:
        "2-digit",

      minute:
        "2-digit",
    }
  ).format(
    date
  );
}

function getSessionLabel(
  number,
  id
) {
  const raw =
    number ||
    id;

  const numeric =
    Number(raw);

  if (
    Number.isFinite(
      numeric
    )
  ) {
    return `TS-${String(
      numeric
    ).padStart(
      4,
      "0"
    )}`;
  }

  return `TS-${raw}`;
}

function getPointName(
  point
) {
  return (
    point?.name ||
    point?.location ||
    point?.city ||
    point?.address ||
    "Localização não definida"
  );
}

function getConnectionSignature(
  connection
) {
  return [
    connection?.fromPoint
      ?.id,
    connection?.fromPoint
      ?.lat,
    connection?.fromPoint
      ?.lng,
    connection?.toPoint?.id,
    connection?.toPoint
      ?.lat,
    connection?.toPoint
      ?.lng,
  ].join(
    ":"
  );
}

function mergePlannerDrivers(
  nextDrivers,
  previousDrivers
) {
  const previousByKey =
    new Map(
      (
        Array.isArray(
          previousDrivers
        )
          ? previousDrivers
          : []
      ).map(
        (
          driver
        ) => [
          driver.driverKey,
          driver,
        ]
      )
    );

  return (
    Array.isArray(
      nextDrivers
    )
      ? nextDrivers
      : []
  ).map(
    (
      driver
    ) => {
      const previousDriver =
        previousByKey.get(
          driver.driverKey
        );

      if (
        !previousDriver
      ) {
        return driver;
      }

      const previousConnections =
        new Map(
          (
            previousDriver
              .connections ||
            []
          ).map(
            (
              connection
            ) => [
              connection.id,
              connection,
            ]
          )
        );

      return {
        ...driver,

        connections:
          (
            driver.connections ||
            []
          ).map(
            (
              connection
            ) => {
              const previousConnection =
                previousConnections.get(
                  connection.id
                );

              if (
                !previousConnection ||
                getConnectionSignature(
                  previousConnection
                ) !==
                  getConnectionSignature(
                    connection
                  )
              ) {
                return connection;
              }

              return {
                ...connection,

                route:
                  previousConnection.route ||
                  null,
              };
            }
          ),
      };
    }
  );
}

function normalizeRouteResponse(
  data
) {
  const route =
    Array.isArray(
      data?.routes
    )
      ? data.routes[0]
      : null;

  if (!route) {
    return null;
  }

  let geometry =
    null;

  if (
    route.geometry?.type ===
    "Feature"
  ) {
    geometry =
      route.geometry;
  } else if (
    route.geometry?.type ===
    "LineString"
  ) {
    geometry = {
      type:
        "Feature",

      geometry:
        route.geometry,

      properties: {},
    };
  }

  if (!geometry) {
    return null;
  }

  return {
    distanceKm:
      route.distanceKm ??
      route.distance_km ??
      null,

    durationSeconds:
      route.durationSeconds ??
      route.duration_seconds ??
      null,

    geometry,

    source:
      route.source ||
      data?.provider ||
      "VALHALLA",
  };
}

function samePoint(
  first,
  second
) {
  if (
    !first ||
    !second
  ) {
    return false;
  }

  return (
    Math.abs(
      Number(first.lat) -
      Number(second.lat)
    ) <
      0.000001 &&
    Math.abs(
      Number(first.lng) -
      Number(second.lng)
    ) <
      0.000001
  );
}

function createZeroDistanceRoute(
  connection
) {
  return {
    distanceKm:
      0,

    durationSeconds:
      0,

    source:
      "SAME_POINT",

    geometry: {
      type:
        "Feature",

      geometry: {
        type:
          "LineString",

        coordinates: [
          [
            Number(
              connection
                .fromPoint
                .lng
            ),

            Number(
              connection
                .fromPoint
                .lat
            ),
          ],
          [
            Number(
              connection
                .toPoint
                .lng
            ),

            Number(
              connection
                .toPoint
                .lat
            ),
          ],
        ],
      },

      properties: {},
    },
  };
}

function getConnectionMarginSeconds(
  connection
) {
  const available =
    Number(
      connection
        ?.availableSeconds
    );

  const duration =
    Number(
      connection?.route
        ?.durationSeconds
    );

  if (
    !Number.isFinite(
      available
    ) ||
    !Number.isFinite(
      duration
    )
  ) {
    return null;
  }

  return (
    available -
    duration
  );
}

function getConnectionStatus(
  connection
) {
  const available =
    Number(
      connection
        ?.availableSeconds
    );

  if (
    Number.isFinite(
      available
    ) &&
    available < 0
  ) {
    return "CONFLICT";
  }

  const margin =
    getConnectionMarginSeconds(
      connection
    );

  if (
    Number.isFinite(
      margin
    )
  ) {
    return margin < 0
      ? "CONFLICT"
      : "OK";
  }

  return "PENDING";
}

function getConnectionGeometry(
  connection
) {
  const geometry =
    connection?.route
      ?.geometry;

  if (
    geometry?.type ===
      "Feature" &&
    geometry.geometry
      ?.type ===
      "LineString" &&
    Array.isArray(
      geometry.geometry.coordinates
    )
  ) {
    return geometry.geometry;
  }

  if (
    geometry?.type ===
      "LineString" &&
    Array.isArray(
      geometry.coordinates
    )
  ) {
    return geometry;
  }

  /*
   * Never invent a straight-line road route.
   *
   * If Valhalla has not returned a route yet,
   * there is no route to draw.
   */
  return null;
}

function getNearestRouteCoordinateIndex(
  coordinates,
  point
) {
  const pointLat =
    Number(
      point?.lat
    );

  const pointLng =
    Number(
      point?.lng
    );

  if (
    !Number.isFinite(
      pointLat
    ) ||
    !Number.isFinite(
      pointLng
    ) ||
    !Array.isArray(
      coordinates
    )
  ) {
    return -1;
  }

  let nearestIndex =
    -1;

  let nearestDistance =
    Number.POSITIVE_INFINITY;

  coordinates.forEach(
    (
      coordinate,
      index
    ) => {
      const routeLng =
        Number(
          coordinate?.[0]
        );

      const routeLat =
        Number(
          coordinate?.[1]
        );

      if (
        !Number.isFinite(
          routeLat
        ) ||
        !Number.isFinite(
          routeLng
        )
      ) {
        return;
      }

      const latitudeScale =
        Math.cos(
          (
            (
              pointLat +
              routeLat
            ) /
            2
          ) *
            (
              Math.PI /
              180
            )
        );

      const deltaLng =
        (
          routeLng -
          pointLng
        ) *
        latitudeScale;

      const deltaLat =
        routeLat -
        pointLat;

      const distance =
        (
          deltaLng *
          deltaLng
        ) +
        (
          deltaLat *
          deltaLat
        );

      if (
        distance <
        nearestDistance
      ) {
        nearestDistance =
          distance;

        nearestIndex =
          index;
      }
    }
  );

  return nearestIndex;
}

function buildSessionRoutesFeatureCollection(
  sessionDetails,
  editedSessionId = null,
  editedRoute = null
) {
  const features =
    [];

  (
    Array.isArray(
      sessionDetails
    )
      ? sessionDetails
      : []
  ).forEach(
    (
      {
        session,
        points,
      }
    ) => {
      const isEditing =
        String(
          session.id
        ) ===
        String(
          editedSessionId
        );

      const previewGeometry =
        isEditing &&
        editedRoute
          ?.geometry
          ?.geometry
          ?.type ===
          "LineString"
          ? editedRoute
              .geometry
              .geometry
          : null;

      const geometry =
        previewGeometry ||
        session?.geometry
          ?.geometry;

      const assignedPoints =
        Array.isArray(
          points
        )
          ? points
          : [];

      if (
        geometry?.type !==
          "LineString" ||
        !Array.isArray(
          geometry.coordinates
        ) ||
        geometry.coordinates
          .length <
          2 ||
        assignedPoints.length <
          2
      ) {
        return;
      }

      const firstPoint =
        assignedPoints[0];

      const lastPoint =
        assignedPoints[
          assignedPoints.length -
            1
        ];

      const firstIndex =
        getNearestRouteCoordinateIndex(
          geometry.coordinates,
          firstPoint
        );

      const lastIndex =
        getNearestRouteCoordinateIndex(
          geometry.coordinates,
          lastPoint
        );

      if (
        firstIndex < 0 ||
        lastIndex < 0
      ) {
        return;
      }

      let clippedCoordinates;

      if (
        firstIndex <=
        lastIndex
      ) {
        clippedCoordinates =
          geometry.coordinates
            .slice(
              firstIndex,
              lastIndex + 1
            )
            .map(
              (
                coordinate
              ) => [
                ...coordinate,
              ]
            );
      } else {
        clippedCoordinates =
          geometry.coordinates
            .slice(
              lastIndex,
              firstIndex + 1
            )
            .reverse()
            .map(
              (
                coordinate
              ) => [
                ...coordinate,
              ]
            );
      }

      if (
        clippedCoordinates.length <
        2
      ) {
        return;
      }

      const firstLng =
        Number(
          firstPoint?.lng
        );

      const firstLat =
        Number(
          firstPoint?.lat
        );

      const lastLng =
        Number(
          lastPoint?.lng
        );

      const lastLat =
        Number(
          lastPoint?.lat
        );

      if (
        Number.isFinite(
          firstLng
        ) &&
        Number.isFinite(
          firstLat
        )
      ) {
        clippedCoordinates[0] = [
          firstLng,
          firstLat,
        ];
      }

      if (
        Number.isFinite(
          lastLng
        ) &&
        Number.isFinite(
          lastLat
        )
      ) {
        clippedCoordinates[
          clippedCoordinates.length -
            1
        ] = [
          lastLng,
          lastLat,
        ];
      }

      const baseProperties = {
        sessionId:
          String(
            session.id
          ),

        transportSessionNumber:
          session
            .transportSessionNumber ??
          "",

        editing:
          isEditing,

        historical:
          Boolean(
            session
              ?.isHistoricalAnchor
          ),
      };

      const completedPoints =
        assignedPoints.filter(
          isPointCompleted
        );

      const lastCompletedPoint =
        completedPoints.length
          ? completedPoints[
              completedPoints.length -
                1
            ]
          : null;

      if (
        !lastCompletedPoint
      ) {
        features.push({
          type:
            "Feature",

          geometry: {
            type:
              "LineString",

            coordinates:
              clippedCoordinates,
          },

          properties: {
            ...baseProperties,

            completed:
              false,
          },
        });

        return;
      }

      const completedRouteIndex =
        getNearestRouteCoordinateIndex(
          clippedCoordinates,
          lastCompletedPoint
        );

      /*
       * If the first visible point is the only completed
       * point, there is no visible route before it to paint.
       */
      if (
        completedRouteIndex <= 0
      ) {
        features.push({
          type:
            "Feature",

          geometry: {
            type:
              "LineString",

            coordinates:
              clippedCoordinates,
          },

          properties: {
            ...baseProperties,

            completed:
              false,
          },
        });

        return;
      }

      const completedCoordinates =
        clippedCoordinates.slice(
          0,
          completedRouteIndex +
            1
        );

      const remainingCoordinates =
        clippedCoordinates.slice(
          completedRouteIndex
        );

      if (
        completedCoordinates.length >=
        2
      ) {
        features.push({
          type:
            "Feature",

          geometry: {
            type:
              "LineString",

            coordinates:
              completedCoordinates,
          },

          properties: {
            ...baseProperties,

            completed:
              true,
          },
        });
      }

      if (
        remainingCoordinates.length >=
        2 &&
        completedRouteIndex <
          clippedCoordinates.length -
            1
      ) {
        features.push({
          type:
            "Feature",

          geometry: {
            type:
              "LineString",

            coordinates:
              remainingCoordinates,
          },

          properties: {
            ...baseProperties,

            completed:
              false,
          },
        });
      }
    }
  );

  return {
    type:
      "FeatureCollection",

    features: features.map((feature) => ({
      ...feature,
      properties: {
        ...feature.properties,
        routePart:
          feature.properties.completed || feature.properties.historical
            ? "completed"
            : "remaining",
        selected: true,
      },
    })),
  };
}


function getSessionPointLabel(
  point
) {
  const type =
    String(
      point?.type ||
      ""
    )
      .trim()
      .toUpperCase();

  if (
    type === "PICKUP"
  ) {
    return "C";
  }

  if (
    type === "DELIVERY"
  ) {
    return "D";
  }

  if (
    type === "ORIGIN"
  ) {
    return "A";
  }

  if (
    type === "DESTINATION"
  ) {
    return "B";
  }

  if (
    type ===
    "STRATEGICPOINT"
  ) {
    return "E";
  }

  if (
    type === "VIA"
  ) {
    return "V";
  }

  if (
    type === "FUEL"
  ) {
    return "F";
  }

  return String(
    point?.sequence ||
    ""
  );
}

function getSessionOperationalPoints(
  session
) {
  if (
    Array.isArray(
      session?.operationalPoints
    ) &&
    session.operationalPoints
      .length
  ) {
    return session.operationalPoints;
  }

  return Array.isArray(
    session?.points
  )
    ? session.points
    : [];
}

function getHistoricalArrayPayload(
  data
) {
  if (
    Array.isArray(
      data
    )
  ) {
    return data;
  }

  const candidates =
    [
      data?.items,
      data?.rows,
      data?.pickupDeliveries,
      data?.data,
    ];

  const found =
    candidates.find(
      Array.isArray
    );

  if (found) {
    return found;
  }

  if (
    data &&
    typeof data ===
      "object"
  ) {
    const nested =
      Object.values(
        data
      ).filter(
        Array.isArray
      );

    if (
      nested.length
    ) {
      return nested.flat();
    }
  }

  return [];
}

function getHistoricalPointSessionId(
  row
) {
  const rawId =
    row?.transportsessionId ??
    row?.transportSessionId ??
    row?.transportsession_id ??
    row?.transport_session_id ??
    row?.transportSession?.id ??
    null;

  const id =
    Number(
      rawId
    );

  return (
    Number.isInteger(
      id
    ) &&
    id > 0
      ? id
      : null
  );
}

function normalizeHistoricalCoordinates(
  row
) {
  if (!row) {
    return null;
  }

  const directLat =
    Number(
      row?.lat ??
      row?.latitude ??
      row?.placeLatitude ??
      row?.latestStatusEvent
        ?.placeLatitude
    );

  const directLng =
    Number(
      row?.lng ??
      row?.lon ??
      row?.longitude ??
      row?.placeLongitude ??
      row?.latestStatusEvent
        ?.placeLongitude
    );

  if (
    Number.isFinite(
      directLat
    ) &&
    Number.isFinite(
      directLng
    ) &&
    Math.abs(
      directLat
    ) <= 90 &&
    Math.abs(
      directLng
    ) <= 180
  ) {
    return {
      lat:
        directLat,

      lng:
        directLng,
    };
  }

  let raw =
    row?.coordinates;

  if (
    typeof raw ===
    "string"
  ) {
    try {
      raw =
        JSON.parse(
          raw
        );
    } catch {
      raw =
        String(
          raw
        ).match(
          /-?\d+(?:\.\d+)?/g
        );
    }
  }

  if (
    Array.isArray(
      raw
    ) &&
    raw.length >= 2
  ) {
    const first =
      Number(
        raw[0]
      );

    const second =
      Number(
        raw[1]
      );

    if (
      Number.isFinite(
        first
      ) &&
      Number.isFinite(
        second
      )
    ) {
      if (
        Math.abs(
          first
        ) <= 90 &&
        Math.abs(
          second
        ) <= 180
      ) {
        return {
          lat:
            first,

          lng:
            second,
        };
      }

      if (
        Math.abs(
          first
        ) <= 180 &&
        Math.abs(
          second
        ) <= 90
      ) {
        return {
          lat:
            second,

          lng:
            first,
        };
      }
    }
  }

  if (
    raw &&
    typeof raw ===
      "object"
  ) {
    return normalizeHistoricalCoordinates(
      raw
    );
  }

  return null;
}

function normalizeHistoricalAnchorPoint(
  row,
  index,
  fallbackSessionId
) {
  const coordinates =
    normalizeHistoricalCoordinates(
      row
    );

  if (!coordinates) {
    return null;
  }

  const originalStatus =
    String(
      row?.status ||
      ""
    )
      .trim()
      .toUpperCase();

  return {
    ...row,

    id:
      row?.id ??
      `historical-point-${index}`,

    transportsessionId:
      getHistoricalPointSessionId(
        row
      ) ??
      fallbackSessionId,

    sequence:
      Number(
        row?.sequence
      ) ||
      index + 1,

    type:
      String(
        row?.type ||
        row?.pointType ||
        "STRATEGICPOINT"
      )
        .trim()
        .toUpperCase(),

    originalStatus,

    /*
     * The retained session is already completed.
     * Its points must therefore be displayed as
     * completed in the Route Planner.
     */
    status:
      "COMPLETED",

    name:
      row?.location ||
      row?.name ||
      row?.city ||
      null,

    address:
      row?.address ||
      null,

    city:
      row?.city ||
      row?.placeCity ||
      null,

    country:
      row?.country ||
      row?.countryName ||
      row?.placeCountry ||
      null,

    countryCode:
      row?.countryCode ||
      row?.country_code ||
      row?.placeCountryCode ||
      null,

    postalCode:
      row?.postalCode ||
      row?.postcode ||
      row?.zipCode ||
      row?.zip_code ||
      row?.placePostalCode ||
      null,

    date:
      row?.date ||
      null,

    hour:
      row?.hour ||
      null,

    substatus:
      row?.latestStatusEvent
        ?.substatus ||
      row?.substatus ||
      null,

    truckPlate:
      row?.truckPlate ||
      row?.truck?.plate ||
      row?.truck
        ?.truckPlate ||
      row?.truckArrivalPlate ||
      row?.truckDeparturePlate ||
      null,

    trailerPlate:
      row?.trailerPlate ||
      row?.trailer
        ?.trailerPlate ||
      row?.trailer?.plate ||
      row?.trailerArrivalPlate ||
      row?.trailerDeparturePlate ||
      null,

    driverName:
      row?.driverName ||
      row?.driver?.name ||
      row?.driverNameArrival ||
      row?.driverNameDeparture ||
      null,

    driverNameArrival:
      row?.driverNameArrival ||
      null,

    driverNameDeparture:
      row?.driverNameDeparture ||
      null,

    lat:
      coordinates.lat,

    lng:
      coordinates.lng,
  };
}

function normalizeHistoricalGeometry(
  value
) {
  if (!value) {
    return null;
  }

  let parsed =
    value;

  if (
    typeof value ===
    "string"
  ) {
    try {
      parsed =
        JSON.parse(
          value
        );
    } catch {
      return null;
    }
  }

  if (
    parsed?.type ===
      "Feature" &&
    parsed?.geometry
      ?.type ===
      "LineString" &&
    Array.isArray(
      parsed.geometry
        .coordinates
    )
  ) {
    return parsed;
  }

  if (
    parsed?.type ===
      "LineString" &&
    Array.isArray(
      parsed.coordinates
    )
  ) {
    return {
      type:
        "Feature",

      geometry:
        parsed,

      properties:
        {},
    };
  }

  return null;
}

function parseRouteSnapshotObject(
  value
) {
  if (!value) {
    return null;
  }

  if (
    typeof value ===
    "object"
  ) {
    return value;
  }

  if (
    typeof value !==
    "string"
  ) {
    return null;
  }

  try {
    const parsed =
      JSON.parse(
        value
      );

    return (
      parsed &&
      typeof parsed ===
        "object"
        ? parsed
        : null
    );
  } catch {
    return null;
  }
}

function normalizeEditableSessionRoute(
  data
) {
  const route =
    Array.isArray(
      data?.routes
    )
      ? data.routes[0]
      : null;

  if (!route) {
    return null;
  }

  const geometry =
    route.geometry?.type ===
    "Feature"
      ? route.geometry
      : route.geometry?.type ===
          "LineString"
        ? {
            type:
              "Feature",

            geometry:
              route.geometry,

            properties:
              {},
          }
        : null;

  if (
    geometry
      ?.geometry
      ?.type !==
    "LineString"
  ) {
    return null;
  }

  return {
    id:
      route.id ||
      "edited-session-route",

    distanceKm:
      route.distanceKm ??
      route.distance_km ??
      null,

    durationSeconds:
      route.durationSeconds ??
      route.duration_seconds ??
      null,

    durationNoTrafficSeconds:
      route.durationNoTrafficSeconds ??
      route.duration_no_traffic_seconds ??
      null,

    routeName:
      route.routeName ??
      route.route_name ??
      null,

    roadNames:
      route.roadNames ??
      route.road_names ??
      [],

    hasTolls:
      route.hasTolls ??
      route.has_tolls ??
      false,

    tollText:
      route.tollText ??
      route.toll_text ??
      null,

    tollDistanceKm:
      route.tollDistanceKm ??
      route.toll_distance_km ??
      null,

    tollRoadNames:
      route.tollRoadNames ??
      route.toll_road_names ??
      [],

    tollAnalysisStatus:
      route.tollAnalysisStatus ??
      route.toll_analysis_status ??
      null,

    tollEstimate:
      route.tollEstimate ??
      route.toll_estimate ??
      null,

    hasHighways:
      route.hasHighways ??
      route.has_highways ??
      false,

    geometry,

    source:
      route.source ||
      data?.provider ||
      "VALHALLA",
  };
}

function getEditableSessionPoints(
  session
) {
  return getSessionOperationalPoints(
    session
  )
    .map(
      (
        point,
        index
      ) => {
        const lat =
          Number(
            point?.lat
          );

        const lng =
          Number(
            point?.lng
          );

        if (
          !Number.isFinite(
            lat
          ) ||
          !Number.isFinite(
            lng
          ) ||
          Math.abs(
            lat
          ) > 90 ||
          Math.abs(
            lng
          ) > 180
        ) {
          return null;
        }

        return {
          ...point,

          lat,
          lng,

          routeSequence:
            Number.isFinite(
              Number(
                point?.sequence
              )
            )
              ? Number(
                  point.sequence
                )
              : index + 1,
        };
      }
    )
    .filter(
      Boolean
    )
    .sort(
      (
        first,
        second
      ) =>
        first.routeSequence -
        second.routeSequence
    );
}

function getSavedSessionViaPoints(
  snapshotValue
) {
  const snapshot =
    parseRouteSnapshotObject(
      snapshotValue
    );

  return (
    Array.isArray(
      snapshot?.points
    )
      ? snapshot.points
      : []
  )
    .filter(
      (
        point
      ) =>
        String(
          point?.source ||
            ""
        )
          .trim()
          .toUpperCase() ===
        "MANUAL_VIA_POINT"
    )
    .map(
      (
        point,
        index
      ) => ({
        id:
          point.id ||
          `saved-via-${index}-${point.lat}-${point.lng}`,

        lat:
          Number(
            point.lat
          ),

        lng:
          Number(
            point.lng
          ),

        segmentIndex:
          Number(
            point
              .segmentIndex
          ) ||
          0,

        order:
          Number(
            point.order
          ) ||
          index + 1,
      })
    )
    .filter(
      (
        point
      ) =>
        Number.isFinite(
          point.lat
        ) &&
        Number.isFinite(
          point.lng
        )
    );
}

function getNearestSessionSegmentIndex({
  map,
  points,
  lngLat,
}) {
  if (
    !map ||
    points.length < 2
  ) {
    return 0;
  }

  const target =
    map.project([
      Number(
        lngLat.lng
      ),
      Number(
        lngLat.lat
      ),
    ]);

  let bestIndex =
    0;

  let bestDistance =
    Number.POSITIVE_INFINITY;

  points
    .slice(
      0,
      -1
    )
    .forEach(
      (
        point,
        index
      ) => {
        const nextPoint =
          points[
            index + 1
          ];

        const start =
          map.project([
            point.lng,
            point.lat,
          ]);

        const end =
          map.project([
            nextPoint.lng,
            nextPoint.lat,
          ]);

        const dx =
          end.x -
          start.x;

        const dy =
          end.y -
          start.y;

        const lengthSquared =
          dx * dx +
          dy * dy;

        const ratio =
          lengthSquared > 0
            ? Math.max(
                0,
                Math.min(
                  1,
                  (
                    (
                      target.x -
                      start.x
                    ) *
                      dx +
                    (
                      target.y -
                      start.y
                    ) *
                      dy
                  ) /
                    lengthSquared
                )
              )
            : 0;

        const closestX =
          start.x +
          ratio * dx;

        const closestY =
          start.y +
          ratio * dy;

        const distance =
          Math.hypot(
            target.x -
              closestX,
            target.y -
              closestY
          );

        if (
          distance <
          bestDistance
        ) {
          bestDistance =
            distance;

          bestIndex =
            index;
        }
      }
    );

  return bestIndex;
}

function buildEditableRoutingLocations({
  session,
  viaPoints,
}) {
  const points =
    getEditableSessionPoints(
      session
    );

  const viasBySegment =
    new Map();

  (
    Array.isArray(
      viaPoints
    )
      ? viaPoints
      : []
  ).forEach(
    (
      viaPoint,
      index
    ) => {
      const segmentIndex =
        Math.min(
          Math.max(
            Number(
              viaPoint
                ?.segmentIndex
            ) ||
              0,
            0
          ),
          Math.max(
            points.length -
              2,
            0
          )
        );

      const current =
        viasBySegment.get(
          segmentIndex
        ) ||
        [];

      current.push({
        ...viaPoint,

        order:
          Number(
            viaPoint
              ?.order
          ) ||
          index + 1,
      });

      viasBySegment.set(
        segmentIndex,
        current
      );
    }
  );

  const locations =
    [];

  points.forEach(
    (
      point,
      index
    ) => {
      locations.push({
        id:
          point.id,

        pickupDeliveryId:
          point
            .pickupDeliveryId ??
          point.id ??
          null,

        type:
          point.type,

        lat:
          point.lat,

        lon:
          point.lng,

        source:
          "SESSION_POINT",
      });

      if (
        index >=
        points.length - 1
      ) {
        return;
      }

      (
        viasBySegment.get(
          index
        ) ||
        []
      )
        .sort(
          (
            first,
            second
          ) =>
            Number(
              first.order
            ) -
            Number(
              second.order
            )
        )
        .forEach(
          (
            viaPoint,
            viaIndex
          ) => {
            locations.push({
              id:
                viaPoint.id,

              pickupDeliveryId:
                null,

              type:
                "VIA",

              lat:
                Number(
                  viaPoint.lat
                ),

              lon:
                Number(
                  viaPoint.lng
                ),

              source:
                "MANUAL_VIA_POINT",

              segmentIndex:
                index,

              order:
                Number(
                  viaPoint.order
                ) ||
                viaIndex + 1,
            });
          }
        );
    }
  );

  return locations;
}

function buildEditableSnapshotPoints(
  routingLocations
) {
  return routingLocations.map(
    (
      location,
      index
    ) => {
      const isFirst =
        index === 0;

      const isLast =
        index ===
        routingLocations.length -
          1;

      const pickupDeliveryId =
        location
          ?.pickupDeliveryId ??
        null;

      return {
        pickupDeliveryId,

        type:
          isFirst
            ? "ORIGIN"
            : isLast
              ? "DESTINATION"
              : "VIA",

        operationalType:
          location?.type ||
          null,

        sequence:
          index + 1,

        lat:
          Number(
            location.lat
          ),

        lng:
          Number(
            location.lon
          ),

        source:
          location?.source ||
          (
            pickupDeliveryId
              ? "SESSION_POINT"
              : "MANUAL_VIA_POINT"
          ),

        segmentIndex:
          location
            ?.segmentIndex ??
          null,

        order:
          location?.order ??
          index + 1,

        name:
          location?.name ||
          null,

        address:
          location?.address ||
          null,
      };
    }
  );
}

function createRouteEditViaPreviewElement() {
  const element =
    document.createElement(
      "div"
    );

  element.className =
    "driver-route-planner__route-via-marker is-preview";

  element.innerHTML =
    "<span>•</span>";

  element.style.pointerEvents =
    "none";

  return element;
}

function normalizeDriverName(
  value
) {
  return String(
    value || ""
  )
    .trim()
    .toLowerCase()
    .replace(
      /\s+/g,
      " "
    );
}

function isPointAssignedToDriver(
  point,
  driver
) {
  const driverName =
    normalizeDriverName(
      driver?.driverName ||
      driver?.name
    );

  if (!driverName) {
    return false;
  }

  const pointDriverNames =
    [
      point?.driverName,
      point?.driver?.name,
      point?.driverNameArrival,
      point?.driverNameDeparture,
    ]
      .map(
        normalizeDriverName
      )
      .filter(Boolean);

  return pointDriverNames.includes(
    driverName
  );
}

function getPointChronologicalValue(
  point
) {
  const dateValue =
    String(
      point?.date ||
      ""
    ).trim();

  const hourValue =
    String(
      point?.hour ||
      "00:00"
    ).trim();

  let year =
    null;

  let month =
    null;

  let day =
    null;

  const isoMatch =
    dateValue.match(
      /^(\d{4})-(\d{2})-(\d{2})/
    );

  if (isoMatch) {
    year =
      Number(
        isoMatch[1]
      );

    month =
      Number(
        isoMatch[2]
      );

    day =
      Number(
        isoMatch[3]
      );
  } else {
    const portugueseMatch =
      dateValue.match(
        /^(\d{2})\/(\d{2})\/(\d{4})/
      );

    if (
      portugueseMatch
    ) {
      day =
        Number(
          portugueseMatch[1]
        );

      month =
        Number(
          portugueseMatch[2]
        );

      year =
        Number(
          portugueseMatch[3]
        );
    }
  }

  if (
    !Number.isInteger(
      year
    ) ||
    !Number.isInteger(
      month
    ) ||
    !Number.isInteger(
      day
    )
  ) {
    return Number.POSITIVE_INFINITY;
  }

  const hourMatch =
    hourValue.match(
      /^(\d{1,2}):(\d{2})/
    );

  const hours =
    hourMatch
      ? Number(
          hourMatch[1]
        )
      : 0;

  const minutes =
    hourMatch
      ? Number(
          hourMatch[2]
        )
      : 0;

  const value =
    new Date(
      year,
      month - 1,
      day,
      hours,
      minutes,
      0,
      0
    ).getTime();

  return Number.isFinite(
    value
  )
    ? value
    : Number.POSITIVE_INFINITY;
}

function getDriverAssignedTimelineRows(
  driver,
  sessions
) {
  if (!driver) {
    return [];
  }

  const sessionIds =
    new Set(
      (
        driver.sessions ||
        []
      ).map(
        (
          session
        ) =>
          String(
            session.id
          )
      )
    );

  return (
    Array.isArray(
      sessions
    )
      ? sessions
      : []
  )
    .filter(
      (
        session
      ) =>
        sessionIds.has(
          String(
            session.id
          )
        )
    )
    .flatMap(
      (
        session
      ) =>
        getSessionOperationalPoints(
          session
        )
          .filter(
            (
              point
            ) =>
              isPointAssignedToDriver(
                point,
                driver
              )
          )
          .map(
            (
              point
            ) => ({
              session,
              point,
            })
          )
    );
}

function getPointTypeLabel(
  point
) {
  const type =
    String(
      point?.type ||
      ""
    )
      .trim()
      .toUpperCase();

  if (
    type === "PICKUP" ||
    type === "ORIGIN"
  ) {
    return "Carga";
  }

  if (
    type === "DELIVERY" ||
    type === "DESTINATION"
  ) {
    return "Descarga";
  }

  if (
    type ===
    "STRATEGICPOINT"
  ) {
    return "Ponto estratégico";
  }

  if (
    type === "FUEL"
  ) {
    return "Combustível";
  }

  if (
    type === "VIA"
  ) {
    return "Via";
  }

  return "Ponto operacional";
}

function getPointTypeChipLabel(
  point
) {
  const type =
    String(
      point?.type ||
      ""
    )
      .trim()
      .toUpperCase();

  if (
    type === "PICKUP" ||
    type === "ORIGIN"
  ) {
    return "CARGA";
  }

  if (
    type === "DELIVERY" ||
    type === "DESTINATION"
  ) {
    return "DESCARGA";
  }

  if (
    type ===
    "STRATEGICPOINT"
  ) {
    return "ESTRAT.";
  }

  if (
    type === "FUEL"
  ) {
    return "FUEL";
  }

  if (
    type === "VIA"
  ) {
    return "VIA";
  }

  return "PONTO";
}

function getPointTypeColor(
  point
) {
  const type =
    String(
      point?.type ||
      ""
    )
      .trim()
      .toUpperCase();

  if (
    type === "PICKUP" ||
    type === "ORIGIN"
  ) {
    return "#0b8dbd";
  }

  if (
    type === "DELIVERY" ||
    type === "DESTINATION"
  ) {
    return "#0f172a";
  }

  if (
    type ===
      "STRATEGICPOINT" ||
    type === "VIA"
  ) {
    return "#9333ea";
  }

  if (
    type === "FUEL"
  ) {
    return "#f59e0b";
  }

  return "#64748b";
}

function getPointSidebarBadgeColor(
  point
) {
  if (
    isPointCompleted(
      point
    )
  ) {
    return "#16a34a";
  }

  const type =
    String(
      point?.type ||
      ""
    )
      .trim()
      .toUpperCase();

  if (
    type === "DELIVERY" ||
    type === "DESTINATION"
  ) {
    return "#334155";
  }

  return getPointTypeColor(
    point
  );
}

function getPointSidebarTypeColor(
  point
) {
  const type =
    String(
      point?.type ||
      ""
    )
      .trim()
      .toUpperCase();

  if (
    type === "DELIVERY" ||
    type === "DESTINATION"
  ) {
    return "#cbd5e1";
  }

  if (
    type === "PICKUP" ||
    type === "ORIGIN"
  ) {
    return "#38bdf8";
  }

  if (
    type ===
      "STRATEGICPOINT" ||
    type === "VIA"
  ) {
    return "#c084fc";
  }

  if (
    type === "FUEL"
  ) {
    return "#fbbf24";
  }

  return "#cbd5e1";
}

function getEffectivePointStatus(
  point
) {
  const status =
    String(
      point?.status ||
      ""
    )
      .trim()
      .toUpperCase();

  const substatus =
    String(
      point?.substatus ||
      ""
    )
      .trim()
      .toUpperCase();

  if (
    status ===
      "COMPLETED" ||
    status ===
      "CANCELLED"
  ) {
    return status;
  }

  return (
    substatus ||
    status ||
    "PENDING"
  );
}

function getPointStatusLabel(
  point
) {
  const status =
    getEffectivePointStatus(
      point
    );

  const labels = {
    PENDING:
      "Pendente",

    COMPLETED:
      "Concluído",

    CANCELLED:
      "Cancelado",

    IN_TRANSIT:
      "Em trânsito",

    IN_TRANSIT_MOVING_TO_PICKUP_LOCATION:
      "A caminho da carga",

    IN_TRANSIT_MOVING_TO_DELIVERY_LOCATION:
      "A caminho da descarga",

    MOVING_TO_PICKUP_LOCATION:
      "A caminho da carga",

    MOVING_TO_DELIVERY_LOCATION:
      "A caminho da descarga",

    AT_PICKUP_LOCATION:
      "Na carga",

    AT_DELIVERY_LOCATION:
      "Na descarga",

    WAITING_TO_LOAD:
      "A aguardar carga",

    WAITING_FOR_MERCHANDISE:
      "A aguardar mercadoria",

    LOADING_IN_PROGRESS:
      "A carregar",

    LOADING_MERCHANDISE:
      "A carregar",

    WAITING_TO_UNLOAD:
      "A aguardar descarga",

    WAITING_FOR_UNLOADING:
      "A aguardar descarga",

    UNLOADING_IN_PROGRESS:
      "A descarregar",

    UNLOADING_MERCHANDISE:
      "A descarregar",

    DELAYED:
      "Atrasado",
  };

  if (
    labels[
      status
    ]
  ) {
    return labels[
      status
    ];
  }

  if (
    status.includes(
      "DELAYED"
    )
  ) {
    return "Atrasado";
  }

  return status
    .toLowerCase()
    .split("_")
    .filter(Boolean)
    .map(
      (
        word
      ) =>
        word.charAt(
          0
        ).toUpperCase() +
        word.slice(
          1
        )
    )
    .join(" ");
}

function getPointStatusPresentation(
  point
) {
  const status =
    getEffectivePointStatus(
      point
    );

  if (
    status ===
    "COMPLETED"
  ) {
    return {
      color:
        "#15803d",

      background:
        "#dcfce7",
    };
  }

  if (
    status ===
      "CANCELLED"
  ) {
    return {
      color:
        "#64748b",

      background:
        "#f1f5f9",
    };
  }

  if (
    status ===
      "DELAYED" ||
    status.includes(
      "DELAYED"
    )
  ) {
    return {
      color:
        "#b91c1c",

      background:
        "#fee2e2",
    };
  }

  if (
    status !==
    "PENDING"
  ) {
    return {
      color:
        "#1d4ed8",

      background:
        "#dbeafe",
    };
  }

  return {
    color:
      "#92400e",

    background:
      "#fef3c7",
  };
}

function isPointCompleted(
  point
) {
  return (
    getEffectivePointStatus(
      point
    ) ===
    "COMPLETED"
  );
}

function isPointFinished(
  point
) {
  const status =
    getEffectivePointStatus(
      point
    );

  return (
    status ===
      "COMPLETED" ||
    status ===
      "CANCELLED"
  );
}

function getPointSequenceLabel(
  point,
  fallbackIndex = 0
) {
  const driverSequence =
    Number(
      point?.driverSequence
    );

  if (
    Number.isInteger(
      driverSequence
    ) &&
    driverSequence > 0
  ) {
    return `${driverSequence}º`;
  }

  const sequence =
    Number(
      point?.sequence
    );

  const safeSequence =
    Number.isInteger(
      sequence
    ) &&
    sequence > 0
      ? sequence
      : fallbackIndex + 1;

  return `${safeSequence}º`;
}

function getPointCountryFlag(
  point
) {
  let code =
    String(
      point?.countryCode ||
      ""
    )
      .trim()
      .toUpperCase();

  if (
    !/^[A-Z]{2}$/.test(
      code
    )
  ) {
    const country =
      String(
        point?.country ||
        ""
      )
        .trim()
        .toLowerCase();

    const countryCodes = {
      portugal:
        "PT",

      espanha:
        "ES",

      spain:
        "ES",

      frança:
        "FR",

      france:
        "FR",

      alemanha:
        "DE",

      germany:
        "DE",

      itália:
        "IT",

      italy:
        "IT",

      bélgica:
        "BE",

      belgium:
        "BE",

      luxemburgo:
        "LU",

      luxembourg:
        "LU",

      suíça:
        "CH",

      switzerland:
        "CH",

      holanda:
        "NL",

      netherlands:
        "NL",
    };

    code =
      countryCodes[
        country
      ] ||
      "";
  }

  if (
    !/^[A-Z]{2}$/.test(
      code
    )
  ) {
    return "";
  }

  return code.replace(
    /[A-Z]/g,
    (character) =>
      String.fromCodePoint(
        127397 +
          character.charCodeAt(
            0
          )
      )
  );
}

function getPointCountryFlagImage(point) {
  const flag = getPointCountryFlag(point);
  const code = Array.from(flag).map((char) =>
    String.fromCharCode(char.codePointAt(0) - 127397)
  ).join("").toLowerCase();

  if (!/^[a-z]{2}$/.test(code)) return null;

  return (
    <img
      src={`https://flagcdn.com/w40/${code}.png`}
      alt=""
      aria-hidden="true"
      width={16}
      height={16}
      style={{
        display: "inline-block",
        width: 16,
        height: 16,
        minWidth: 16,
        objectFit: "cover",
        borderRadius: "50%",
        verticalAlign: "-3px",
      }}
    />
  );
}

function getPointLocationLine(
  point
) {

  const city =
    String(
      point?.city || ""
    ).trim();

  const country =
    String(
      point?.country ||
      ""
    ).trim();

  return [city, country]
    .filter(Boolean)
    .join(", ");
}

function getPointPostalAddressLine(
  point
) {
  const postalCode =
    String(
      point?.postalCode ||
      point?.postcode ||
      point?.zipCode ||
      ""
    ).trim();

  const address =
    String(
      point?.address || ""
    ).trim();

  return [
    postalCode,
    address,
  ]
    .filter(Boolean)
    .join(" · ");
}

function createDriverPointMarkerElement(
  point,
  index,
  {
    initiallyCollapsed = true,
    onCollapsedChange = null,
  } = {}
) {
  const root =
    document.createElement(
      "div"
    );

  root.style.cssText = `
    position: relative;
    width: 1px;
    height: 1px;
    overflow: visible;
    pointer-events: none;
  `;

  const card =
    document.createElement(
      "div"
    );

  const sequence =
    getPointSequenceLabel(
      point,
      index
    );

  const typeLabel =
    getPointTypeLabel(
      point
    );

  const placeName =
    getPointName(
      point
    );

  card.dataset.hoverLabel = placeName;
  card.setAttribute("aria-label", `Expandir ponto: ${placeName}`);

  const locationLine =
    getPointLocationLine(
      point
    ) ||

    String(
      point?.address ||
      ""
    ).trim() ||
    "Localização não definida";

  const typeColor =
    getPointTypeColor(
      point
    );


  const pointCompleted =
    isPointCompleted(
      point
    );

  const pointType = String(point?.type || "").toUpperCase();
  const kind = ["PICKUP", "ORIGIN"].includes(pointType)
    ? "pickup"
    : ["DELIVERY", "DESTINATION"].includes(pointType)
      ? "delivery"
      : "other";


  const statusLabel =
    getPointStatusLabel(
      point
    );

  const statusPresentation =
    getPointStatusPresentation(
      point
    );

  let collapsed =
    Boolean(
      initiallyCollapsed
    );

  const topRow =
    document.createElement(
      "div"
    );

  const pinIcon =
    document.createElement(
      "span"
    );

  const pinImage = kind === "other"
    ? null
    : document.createElement("img");

  if (pinImage) {
    pinImage.src = kind === "pickup"
      ? pickupPinImage
      : deliveryPinImage;
    pinImage.alt = "";
    pinImage.draggable = false;
    pinIcon.appendChild(pinImage);
  } else {
    pinIcon.textContent = sequence;
  }


  const sequenceBadge =
    document.createElement(
      "span"
    );

  sequenceBadge.textContent =
    sequence;

  const typeBadge =
    document.createElement(
      "span"
    );

  typeBadge.textContent =
    typeLabel;

  const toggleButton =
    document.createElement(
      "button"
    );

  toggleButton.type =
    "button";

  toggleButton.setAttribute(
    "aria-label",
    collapsed
      ? "Expandir ponto"
      : "Recolher ponto"
  );

  const name =
    document.createElement(
      "strong"
    );

  name.textContent =
    placeName;

  const location =
    document.createElement(
      "span"
    );

  location.textContent = `📍 ${locationLine}`;


  const statusBadge =
    document.createElement(
      "span"
    );

  statusBadge.textContent =
    statusLabel;


  const applyState =
    () => {
      card.className = collapsed
        ? `ld-map-stop-pin is-${kind}`
        : `driver-route-planner__point-detail is-${kind}`;
      card.tabIndex = collapsed ? 0 : -1;
      card.setAttribute("role", collapsed ? "button" : "group");
      card.setAttribute(
        "aria-label",
        `${collapsed ? "Expandir" : "Detalhes do"} ponto: ${placeName}`
      );

      card.style.cssText =
        collapsed
          ? `
            position: absolute;
            left: 50%;
            bottom: 0;
            transform: translateX(-50%);
            display: flex;
            align-items: center;
            justify-content: center;
            width: ${kind === "other" ? 29 : 44}px;
            height: ${kind === "other" ? 29 : 52}px;
            padding: 0;
            border: ${kind === "other" ? "2px solid #fff" : "0"};
            background: ${kind === "other" ? "#475569" : "transparent"};
            color: #fff;
            box-shadow: none;
            pointer-events: auto;
            cursor: pointer;
          `
          : `

            position: absolute;
            left: 50%;
            bottom: 0;
            transform: translateX(-50%);
            transform-origin: 50% 100%;
            width: 190px;
            display: grid;
            gap: 4px;
            padding: 8px 10px 9px;
            border: 1px solid rgba(15, 23, 42, 0.14);
            border-radius: 14px;
            background: rgba(255, 255, 255, 0.98);
            color: #0f172a;
            box-shadow:
              0 10px 24px rgba(15, 23, 42, 0.18),
              0 0 0 1px rgba(255, 255, 255, 0.72);
            backdrop-filter: blur(10px);
            font-family: inherit;
            pointer-events: auto;
            cursor: default;
          `;

      topRow.style.cssText =
        collapsed
          ? `
            display: inline-flex;
            align-items: center;
            justify-content: center;
            gap: 5px;
            min-width: 0;
          `
          : `
            display: flex;
            align-items: center;
            gap: 6px;
            min-width: 0;
          `;

      pinIcon.style.cssText = `
        display: inline-flex;
        align-items: center;
        justify-content: center;
        width: ${collapsed ? (kind === "other" ? 29 : 44) : 20}px;
        height: ${collapsed ? (kind === "other" ? 29 : 52) : 24}px;
        flex: 0 0 auto;
      `;

      if (pinImage) {
        pinImage.style.width = collapsed ? "44px" : "20px";
        pinImage.style.height = collapsed ? "52px" : "24px";
        pinImage.style.objectFit = "contain";
      }


      sequenceBadge.style.cssText =
        collapsed
          ? `
            display: none;
          `
          : `

            flex: 0 0 auto;
            min-width: 28px;
            display: inline-flex;
            align-items: center;
            justify-content: center;
            padding: 4px 6px;
            border-radius: 9px;
            background: ${
              pointCompleted
                ? "#16a34a"
                : "#eef2f7"
            };
            color: ${
              pointCompleted
                ? "#ffffff"
                : "#334155"
            };
            font-size: 10px;
            font-weight: 850;
            line-height: 1;
          `;

      typeBadge.style.cssText = `
        min-width: 0;
        color: ${typeColor};
        font-size: 10px;
        font-weight: 850;
        line-height: 1.1;
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;
        display: ${
          collapsed
            ? "none"
            : "inline"
        };
      `;

      toggleButton.style.cssText =
        collapsed
          ? `
            width: 14px;
            height: 14px;
            display: inline-flex;
            align-items: center;
            justify-content: center;
            padding: 0;
            margin: 0;
            border: 0;
            background: transparent;
            color: #64748b;
            font-size: 9px;
            font-weight: 900;
            line-height: 1;
            cursor: pointer;
            pointer-events: auto;
          `
          : `
            width: 18px;
            height: 18px;
            display: inline-flex;
            align-items: center;
            justify-content: center;
            flex: 0 0 auto;
            padding: 0;
            margin: 0 0 0 auto;
            border: 0;
            border-radius: 6px;
            background: #f1f5f9;
            color: #64748b;
            font-size: 9px;
            font-weight: 900;
            line-height: 1;
            cursor: pointer;
            pointer-events: auto;
          `;

      toggleButton.textContent =
        collapsed
          ? "▾"
          : "▴";

      toggleButton.setAttribute(
        "aria-label",
        collapsed
          ? "Expandir ponto"
          : "Recolher ponto"
      );

      name.style.cssText = `
        display: ${
          collapsed
            ? "none"
            : "block"
        };
        min-width: 0;
        color: #0f172a;
        font-size: 13px;
        font-weight: 850;
        line-height: 1.12;
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;
      `;

      location.style.cssText = `
        display: ${
          collapsed
            ? "none"
            : "block"
        };
        min-width: 0;
        color: #64748b;
        font-size: 10px;
        font-weight: 700;
        line-height: 1.2;
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;
      `;

      statusBadge.style.cssText = `
        display: ${
          collapsed
            ? "none"
            : "inline-flex"
        };
        align-items: center;
        justify-content: center;
        justify-self: start;
        margin-top: 2px;
        padding: 3px 7px;
        border-radius: 999px;
        background: ${statusPresentation.background};
        color: ${statusPresentation.color};
        font-size: 9px;
        font-weight: 800;
        line-height: 1;
        white-space: nowrap;
      `;
    };

  const stopMapInteraction =
    (
      event
    ) => {
      event.stopPropagation();
    };

  card.addEventListener(
    "pointerdown",
    stopMapInteraction
  );

  card.addEventListener(
    "mousedown",
    stopMapInteraction
  );

  card.addEventListener(
    "touchstart",
    stopMapInteraction,
    {
      passive:
        true,
    }
  );

  card.addEventListener(
    "dblclick",
    stopMapInteraction
  );

  card.addEventListener(
    "click",
    stopMapInteraction
  );

  toggleButton.addEventListener(
    "click",
    (
      event
    ) => {
      event.preventDefault();
      event.stopPropagation();

      collapsed =
        !collapsed;

      applyState();

      if (
        typeof onCollapsedChange ===
        "function"
      ) {
        onCollapsedChange(
          collapsed
        );
      }
    }
  );

  topRow.appendChild(
    pinIcon
  );

  topRow.appendChild(
    sequenceBadge
  );

  topRow.appendChild(
    typeBadge
  );

  topRow.appendChild(
    toggleButton
  );

  card.appendChild(
    topRow
  );

  card.appendChild(
    name
  );

  card.appendChild(
    location
  );

  card.appendChild(
    statusBadge
  );

  root.appendChild(
    card
  );

  applyState();

  return root;
}


function formatOperationalPointDateTime(
  point,
  locale
) {
  if (!point) {
    return "";
  }

  let dateLabel =
    String(
      point?.date || ""
    ).trim();

  const hourLabel =
    String(
      point?.hour || ""
    )
      .trim()
      .slice(0, 5);

  const isoDateMatch =
    dateLabel.match(
      /^(\d{4})-(\d{2})-(\d{2})/
    );

  if (isoDateMatch) {
    const [
      ,
      year,
      month,
      day,
    ] = isoDateMatch;

    try {
      dateLabel =
        new Date(
          Number(year),
          Number(month) - 1,
          Number(day)
        ).toLocaleDateString(
          locale
        );
    } catch {
      dateLabel =
        String(
          point?.date || ""
        ).trim();
    }
  }

  return [
    dateLabel,
    hourLabel,
  ]
    .filter(Boolean)
    .join(" · ");
}

function buildRoutesFeatureCollection(
  driver,
  selectedConnectionId
) {
  const features =
    (
      driver?.connections ||
      []
    )
      .map(


        (
          connection
        ) => {
          const geometry =
            getConnectionGeometry(
              connection
            );

          if (!geometry) {
            return null;
          }

          return {
            type:
              "Feature",

            geometry,

            properties: {
              connectionId:
                connection.id,

              selected:
                String(
                  connection.id
                ) ===
                String(
                  selectedConnectionId
                ),

              completed:
                isPointCompleted(
                  connection
                    ?.toPoint
                ),

              status:
                getConnectionStatus(
                  connection
                ),

              routed:
                Boolean(
                  connection.route
                ),

              routePart:
                isPointCompleted(connection?.toPoint)
                  ? "completed"
                  : "remaining",
            },

          };
        }
      )
      .filter(
        Boolean
      );

  return {
    type:
      "FeatureCollection",

    features,
  };
}

function buildPointsFeatureCollection(
  driver
) {
  const features =
    [];

  (
    driver?.connections ||
    []
  ).forEach(
    (
      connection
    ) => {
      const rows = [
        {
          point:
            connection.fromPoint,

          label:
            getSessionLabel(
              connection
                .fromTransportSessionNumber,
              connection
                .fromTransportSessionId
            ),

          role:
            "FROM",
        },
        {
          point:
            connection.toPoint,

          label:
            getSessionLabel(
              connection
                .toTransportSessionNumber,
              connection
                .toTransportSessionId
            ),

          role:
            "TO",
        },
      ];

      rows.forEach(
        (
          item
        ) => {
          const lat =
            Number(
              item.point?.lat
            );

          const lng =
            Number(
              item.point?.lng
            );

          if (
            !Number.isFinite(
              lat
            ) ||
            !Number.isFinite(
              lng
            )
          ) {
            return;
          }

          features.push({
            type:
              "Feature",

            geometry: {
              type:
                "Point",

              coordinates: [
                lng,
                lat,
              ],
            },

            properties: {
              label:
                item.label,

              role:
                item.role,
            },
          });
        }
      );
    }
  );

  return {
    type:
      "FeatureCollection",

    features,
  };
}


function getDriverMapPadding(map) {
  const canvas = map?.getContainer();

  if (!canvas) {
    return { top: 70, bottom: 70, left: 48, right: 48 };
  }

  const root = canvas.closest(
    ".live-transports-map.driver-route-planner"
  );

  const mapRect = canvas.getBoundingClientRect();
  const width = canvas.clientWidth || mapRect.width;
  const height = canvas.clientHeight || mapRect.height;

  if (!mapRect.width || !width) {
    return { top: 70, bottom: 70, left: 48, right: 48 };
  }

  // Converter medidas visuais, incluindo transform/scale,
  // para os píxeis CSS utilizados pelo MapLibre.
  const scaleX = width / mapRect.width;

  const isVisible = (element) => {
    if (!element?.getClientRects().length) return null;

    const style = window.getComputedStyle(element);

    if (
      style.display === "none" ||
      style.visibility === "hidden"
    ) {
      return null;
    }

    const rect = element.getBoundingClientRect();

    return rect.width > 0 && rect.height > 0
      ? rect
      : null;
  };

  const sidebarRect = root?.classList.contains(
    "is-sidebar-collapsed"
  )
    ? null
    : isVisible(
        root?.querySelector(
          ".live-transports-map__sidebar"
        )
      );

  const panelRect = isVisible(
    root?.querySelector(
      ".live-transports-map__selection.is-driver-unified, .driver-route-planner__selection"
    )
  );

  let left = 48;
  let right = 48;

  if (sidebarRect && sidebarRect.right > mapRect.left) {
    left = Math.max(
      48,
      Math.ceil(
        (sidebarRect.right - mapRect.left) * scaleX
      ) + 22
    );
  }

  if (panelRect && panelRect.left < mapRect.right) {
    right = Math.max(
      48,
      Math.ceil(
        (mapRect.right - panelRect.left) * scaleX
      ) + 22
    );
  }

  // Evitar que os dois paddings eliminem
  // toda a área útil em janelas pequenas.
  const maxPadding = Math.max(0, width - 160);

  if (
    left + right > maxPadding &&
    left + right > 0
  ) {
    const factor = maxPadding / (left + right);

    left = Math.floor(left * factor);
    right = Math.floor(right * factor);
  }

  const verticalPadding = Math.min(
    75,
    Math.max(0, (height - 120) / 2)
  );

  return {
    top: verticalPadding,
    bottom: verticalPadding,
    left,
    right,
  };
}

const DriverRoutePlannerMap = ({
  currentSessions = [],
  currentSessionsLoading = false,
  onRouteSaved,
  onShowSessions,
  SharedPanelHeader,
  renderDriverPanel,
}) => {

  const {
    i18n,
  } =
    useTranslation(
      "map"
    );

  const locale =
    i18n.resolvedLanguage ||
    i18n.language ||
    "pt-PT";

  const initialCacheRef =
    useRef(
      readPlannerCache()
    );

  const mapContainerRef =
    useRef(null);

  const mapRef =
    useRef(null);

  const pointMarkersRef =
    useRef([]);

  const routeEditMarkersRef =
    useRef([]);

  const routeEditDragStateRef =
    useRef(null);

  const routeEditPreviewMarkerRef =
    useRef(null);

  const routeEditPreviewTimerRef =
    useRef(null);

  const routeEditPreviewControllerRef =
    useRef(null);

  const routeEditPreviewLatestRef =
    useRef(null);

  const routeEditPreviewLastRequestAtRef =
    useRef(0);

  const routeEditPreviewRequestIdRef =
    useRef(0);

  const expandedPointMarkersRef =
    useRef(
      new Set()
    );

  const routingInFlightRef =
    useRef(
      new Set()
    );

  const [
    routeEditMode,
    setRouteEditMode,
  ] = useState(false);

  const [
    routeEditSessionId,
    setRouteEditSessionId,
  ] = useState(null);

  const [
    routeEditViaPoints,
    setRouteEditViaPoints,
  ] = useState([]);

  const [
    routeEditPreviewRoute,
    setRouteEditPreviewRoute,
  ] = useState(null);

  const [
    routeEditSaving,
    setRouteEditSaving,
  ] = useState(false);

  const [
    routeEditError,
    setRouteEditError,
  ] = useState(null);

  const [mapReady, setMapReady] =
    useState(false);

  const [
    isSidebarCollapsed,
    setIsSidebarCollapsed,
  ] = useState(false);

  const [searchTerm, setSearchTerm] =
    useState("");

  const [driverPanelMode, setDriverPanelMode] =
    useState("complete");

  const [driverPanelSessionId, setDriverPanelSessionId] =
    useState(null);

  const [isDriverPanelOpen, setIsDriverPanelOpen] =
    useState(true);

  const [drivers, setDrivers] =
    useState(

      () =>
        initialCacheRef
          .current
          ?.drivers ||
        []
    );

  const [
    selectedDriverKey,
    setSelectedDriverKey,
  ] = useState(
    () =>
      initialCacheRef
        .current
        ?.drivers?.[0]
        ?.driverKey ||
      null
  );

  useEffect(() => {
    if (selectedDriverKey) {
      setIsDriverPanelOpen(true);
    }
  }, [selectedDriverKey]);

  const [
    selectedConnectionId,
    setSelectedConnectionId,
  ] = useState(null);


  const [revision, setRevision] =
    useState(
      initialCacheRef
        .current
        ?.revision ||
      null
    );

  const [
    generatedAt,
    setGeneratedAt,
  ] = useState(
    initialCacheRef
      .current
      ?.generatedAt ||
    null
  );

  const [loading, setLoading] =
    useState(
      !initialCacheRef
        .current
        ?.drivers?.length
    );

  const [refreshing, setRefreshing] =
    useState(false);

  const [error, setError] =
    useState(null);

  const [
    historicalAnchorSession,
    setHistoricalAnchorSession,
  ] =
    useState(null);

  const refreshPlanner =
    useCallback(
      async ({
        silent = false,
      } = {}) => {
        if (silent) {
          setRefreshing(
            true
          );
        } else {
          setLoading(
            true
          );
        }

        setError(null);

        try {
          const {
            data,
          } =
            await api.get(
              "/pickupdeliveries/driver-route-planner"
            );

          const nextDrivers =
            Array.isArray(
              data?.drivers
            )
              ? data.drivers
              : [];

          setDrivers(
            (
              previous
            ) =>
              mergePlannerDrivers(
                nextDrivers,
                previous
              )
          );

          setRevision(
            data?.revision ||
            null
          );

          setGeneratedAt(
            data?.generatedAt ||
            new Date().toISOString()
          );
        } catch (
          requestError
        ) {
          console.error(
            "[DriverRoutePlannerMap] load failed:",
            requestError
          );

          setError(
            requestError
              ?.response
              ?.data
              ?.error ||
            "Não foi possível carregar o planeador dos motoristas."
          );
        } finally {
          setLoading(
            false
          );

          setRefreshing(
            false
          );
        }
      },
      []
    );

  useEffect(() => {
    writePlannerCache({
      revision,
      generatedAt,
      drivers,
    });
  }, [
    drivers,
    generatedAt,
    revision,
  ]);

  useEffect(() => {
    refreshPlanner({
      silent:
        Boolean(
          initialCacheRef
            .current
            ?.drivers
            ?.length
        ),
    });
  }, [
    refreshPlanner,
  ]);

  const scopedDrivers =
    useMemo(() => {
      const currentSessionIds =
        new Set(
          (
            Array.isArray(
              currentSessions
            )
              ? currentSessions
              : []
          ).map(
            (
              session
            ) =>
              String(
                session.id
              )
          )
        );

      if (
        !currentSessionIds
          .size
      ) {
        return [];
      }

      return drivers
        .map(
          (
            driver
          ) => {
            const allSessions =
              Array.isArray(
                driver.sessions
              )
                ? driver.sessions
                : [];

            const allConnections =
              Array.isArray(
                driver.connections
              )
                ? driver.connections
                : [];

            const currentDriverSessions =
              allSessions.filter(
                (
                  session
                ) =>
                  currentSessionIds.has(
                    String(
                      session.id
                    )
                  )
              );

            /*
             * No current/future session means there is
             * nothing to plan, so do not keep a historical
             * anchor for this driver.
             */
            if (
              !currentDriverSessions
                .length
            ) {
              return null;
            }

            const currentDriverSessionIds =
              new Set(
                currentDriverSessions.map(
                  (
                    session
                  ) =>
                    String(
                      session.id
                    )
                )
              );

            const sessionOrder =
              new Map(
                allSessions.map(
                  (
                    session,
                    index
                  ) => [
                    String(
                      session.id
                    ),

                    index,
                  ]
                )
              );

            /*
             * Find the connection that enters the current
             * timeline from a session that has already
             * left Current Loads.
             *
             * That FROM session is our single historical
             * continuity anchor.
             */
            const previousConnection =
              allConnections
                .filter(
                  (
                    connection
                  ) => {
                    const fromId =
                      String(
                        connection
                          .fromTransportSessionId ??
                        ""
                      );

                    const toId =
                      String(
                        connection
                          .toTransportSessionId ??
                        ""
                      );

                    return (
                      !currentDriverSessionIds.has(
                        fromId
                      ) &&
                      currentDriverSessionIds.has(
                        toId
                      )
                    );
                  }
                )
                .sort(
                  (
                    first,
                    second
                  ) => {
                    const firstIndex =
                      sessionOrder.get(
                        String(
                          first
                            .toTransportSessionId
                        )
                      ) ??
                      Number.MAX_SAFE_INTEGER;

                    const secondIndex =
                      sessionOrder.get(
                        String(
                          second
                            .toTransportSessionId
                        )
                      ) ??
                      Number.MAX_SAFE_INTEGER;

                    return (
                      firstIndex -
                      secondIndex
                    );
                  }
                )[0] ||
              null;

            const previousSessionId =
              previousConnection
                ?.fromTransportSessionId ??
              null;

            const previousSession =
              previousSessionId !==
              null
                ? (
                    allSessions.find(
                      (
                        session
                      ) =>
                        String(
                          session.id
                        ) ===
                        String(
                          previousSessionId
                        )
                    ) ||
                    {
                      id:
                        previousSessionId,
                    }
                  )
                : null;

            const sessions =
              [
                previousSession
                  ? {
                      ...previousSession,

                      isHistoricalAnchor:
                        true,

                      readOnly:
                        true,
                    }
                  : null,

                ...currentDriverSessions,
              ].filter(
                Boolean
              );

            const visibleSessionIds =
              new Set(
                sessions.map(
                  (
                    session
                  ) =>
                    String(
                      session.id
                    )
                )
              );

            /*
             * Keep:
             * - current -> current transitions
             * - historical anchor -> first current transition
             *
             * Everything older stays hidden.
             */
            const connections =
              allConnections.filter(
                (
                  connection
                ) =>
                  visibleSessionIds.has(
                    String(
                      connection
                        .fromTransportSessionId
                    )
                  ) &&
                  visibleSessionIds.has(
                    String(
                      connection
                        .toTransportSessionId
                    )
                  )
              );

            return {
              ...driver,

              sessions,

              connections,

              currentSessionCount:
                currentDriverSessions
                  .length,

              previousCompletedSessionId:
                previousSessionId,
            };
          }
        )
        .filter(
          Boolean
        );
    }, [
      currentSessions,
      drivers,
    ]);

      useEffect(() => {
    if (
      !scopedDrivers.length
    ) {
      setSelectedDriverKey(
        null
      );

      return;
    }

    setSelectedDriverKey(
      (
        current
      ) => {
        const exists =
          scopedDrivers.some(
            (
              driver
            ) =>
              driver.driverKey ===
              current
          );

        if (exists) {
          return current;
        }

        return scopedDrivers[0]
          .driverKey;
      }
    );
  }, [
    scopedDrivers,
  ]);

  const selectedDriver =
    useMemo(() => {
      return (
        scopedDrivers.find(
          (
            driver
          ) =>
            driver.driverKey ===
            selectedDriverKey
        ) ||
        null
      );
    }, [
      scopedDrivers,
      selectedDriverKey,
    ]);

      useEffect(() => {
    const sessionId =
      Number(
        selectedDriver
          ?.previousCompletedSessionId
      );

    if (
      !Number.isInteger(
        sessionId
      ) ||
      sessionId <= 0
    ) {
      setHistoricalAnchorSession(
        null
      );

      return undefined;
    }

    let cancelled =
      false;

    const historicalShell =
      (
        selectedDriver.sessions ||
        []
      ).find(
        (
          session
        ) =>
          session
            ?.isHistoricalAnchor &&
          String(
            session.id
          ) ===
            String(
              sessionId
            )
      ) ||
      null;

    const loadHistoricalAnchor =
      async () => {
        const [
          sessionResult,
          pointsResult,
        ] =
          await Promise.allSettled([
            api.get(
              `/transportsessions/${sessionId}`
            ),

            api.get(
              "/pickupdeliveries/by-sessions",
              {
                params: {
                  ids:
                    String(
                      sessionId
                    ),
                },
              }
            ),
          ]);

        if (cancelled) {
          return;
        }

        const sessionPayload =
          sessionResult.status ===
          "fulfilled"
            ? sessionResult.value
                .data
            : null;

        const detail =
          sessionPayload
            ?.transportSession ??
          sessionPayload
            ?.session ??
          sessionPayload
            ?.item ??
          sessionPayload
            ?.data ??
          sessionPayload ??
          historicalShell ??
          {};

        const endpointPointRows =
          pointsResult.status ===
          "fulfilled"
            ? getHistoricalArrayPayload(
                pointsResult.value
                  .data
              )
            : [];

        const detailPointRows =
          getSessionOperationalPoints(
            detail
          );

        const shellPointRows =
          getSessionOperationalPoints(
            historicalShell
          );

        const rawPointRows =
          endpointPointRows.length
            ? endpointPointRows
            : detailPointRows.length
              ? detailPointRows
              : shellPointRows;

        const operationalPoints =
          rawPointRows
            .map(
              (
                row,
                index
              ) =>
                normalizeHistoricalAnchorPoint(
                  row,
                  index,
                  sessionId
                )
            )
            .filter(
              Boolean
            )
            .sort(
              (
                first,
                second
              ) =>
                (
                  Number(
                    first
                      .sequence
                  ) ||
                  0
                ) -
                (
                  Number(
                    second
                      .sequence
                  ) ||
                  0
                )
            );

        const routeSnapshot =
          parseRouteSnapshotObject(
            detail?.routeSnapshot ??
            historicalShell
              ?.routeSnapshot
          );

        let geometry =
          normalizeHistoricalGeometry(
            detail?.geometry
          ) ||
          normalizeHistoricalGeometry(
            historicalShell
              ?.geometry
          ) ||
          normalizeHistoricalGeometry(
            routeSnapshot
              ?.route
              ?.geometry
          ) ||
          normalizeHistoricalGeometry(
            routeSnapshot
              ?.geometry
          );

        /*
         * Last-resort road route reconstruction.
         * Normally the stored completed TS geometry
         * should already be available.
         */
        if (
          !geometry &&
          operationalPoints
            .length >= 2
        ) {
          try {
            const {
              data,
            } =
              await api.post(
                "/maps/route",
                {
                  costing:
                    "truck",

                  alternatives:
                    0,

                  includeTollEstimate:
                    false,

                  locations:
                    operationalPoints.map(
                      (
                        point,
                        index
                      ) => ({
                        id:
                          point.id ||
                          `historical-point-${index}`,

                        type:
                          point.type,

                        sequence:
                          index + 1,

                        lat:
                          point.lat,

                        lon:
                          point.lng,
                      })
                    ),
                }
              );

            const routed =
              normalizeEditableSessionRoute(
                data
              );

            geometry =
              routed?.geometry ||
              null;
          } catch (
            routeError
          ) {
            console.warn(
              "[DriverRoutePlannerMap] historical route failed:",
              routeError?.message
            );
          }
        }

        if (cancelled) {
          return;
        }

        setHistoricalAnchorSession({
          ...historicalShell,
          ...detail,

          id:
            sessionId,

          transportSessionNumber:
            detail
              ?.transportSessionNumber ??
            detail
              ?.transportsessionNumber ??
            historicalShell
              ?.transportSessionNumber ??
            null,

          status:
            "COMPLETED",

          routeName:
            detail
              ?.routeName ??
            historicalShell
              ?.routeName ??
            routeSnapshot
              ?.name ??
            null,

          routeNumber:
            detail
              ?.routeNumber ??
            historicalShell
              ?.routeNumber ??
            routeSnapshot
              ?.routeNumber ??
            null,

          routeSnapshot,

          points:
            operationalPoints,

          operationalPoints,

          geometry,

          isHistoricalAnchor:
            true,

          readOnly:
            true,
        });
      };

    loadHistoricalAnchor()
      .catch(
        (
          historicalError
        ) => {
          if (cancelled) {
            return;
          }

          console.warn(
            "[DriverRoutePlannerMap] historical anchor failed:",
            historicalError
          );

          setHistoricalAnchorSession(
            null
          );
        }
      );

    return () => {
      cancelled =
        true;
    };
  }, [
    selectedDriver
      ?.driverName,
    selectedDriver
      ?.previousCompletedSessionId,
  ]);

  const selectedDriverSessions =
    useMemo(() => {
      if (
        !selectedDriver
      ) {
        return [];
      }

      const currentSessionIds =
        new Set(
          (
            selectedDriver.sessions ||
            []
          )
            .filter(
              (
                session
              ) =>
                !session
                  ?.isHistoricalAnchor
            )
            .map(
              (
                session
              ) =>
                String(
                  session.id
                )
            )
        );

      const sessions =
        (
          Array.isArray(
            currentSessions
          )
            ? currentSessions
            : []
        ).filter(
          (
            session
          ) =>
            currentSessionIds.has(
              String(
                session.id
              )
            )
        );

      if (
        historicalAnchorSession &&
        String(
          historicalAnchorSession
            .id
        ) ===
          String(
            selectedDriver
              .previousCompletedSessionId
          )
      ) {
        sessions.unshift(
          historicalAnchorSession
        );
      }

      return sessions;
    }, [
      currentSessions,
      historicalAnchorSession,
      selectedDriver,
    ]);

  const routeEditSession =
    useMemo(() => {
      return (
        selectedDriverSessions.find(
          (
            session
          ) =>
            !session
              ?.isHistoricalAnchor &&
            String(
              session.id
            ) ===
            String(
              routeEditSessionId
            )
        ) ||
        null
      );
    }, [
      routeEditSessionId,
      selectedDriverSessions,
    ]);

  const clearRouteEditPreviewRequest =
    useCallback(
      () => {
        if (
          routeEditPreviewTimerRef.current !==
          null
        ) {
          window.clearTimeout(
            routeEditPreviewTimerRef.current
          );

          routeEditPreviewTimerRef.current =
            null;
        }

        routeEditPreviewControllerRef
          .current
          ?.abort();

        routeEditPreviewControllerRef.current =
          null;

        routeEditPreviewLatestRef.current =
          null;

        routeEditPreviewRequestIdRef.current +=
          1;
      },
      []
    );

  const queueRouteEditPreview =
    useCallback(
      (
        session,
        viaPoints
      ) => {
        if (!session) {
          return;
        }

        routeEditPreviewLatestRef.current = {
          session,

          viaPoints:
            Array.isArray(
              viaPoints
            )
              ? viaPoints
              : [],
        };

        if (
          routeEditPreviewTimerRef.current !==
          null
        ) {
          return;
        }

        const elapsed =
          Date.now() -
          routeEditPreviewLastRequestAtRef.current;

        const delay =
          Math.max(
            0,
            160 -
              elapsed
          );

        routeEditPreviewTimerRef.current =
          window.setTimeout(
            async () => {
              routeEditPreviewTimerRef.current =
                null;

              const latest =
                routeEditPreviewLatestRef.current;

              if (
                !latest?.session
              ) {
                return;
              }

              const locations =
                buildEditableRoutingLocations({
                  session:
                    latest.session,

                  viaPoints:
                    latest.viaPoints,
                });

              if (
                locations.length < 2 ||
                locations.length > 25
              ) {
                return;
              }

              routeEditPreviewControllerRef
                .current
                ?.abort();

              const controller =
                new AbortController();

              routeEditPreviewControllerRef.current =
                controller;

              const requestId =
                routeEditPreviewRequestIdRef.current +
                1;

              routeEditPreviewRequestIdRef.current =
                requestId;

              routeEditPreviewLastRequestAtRef.current =
                Date.now();

              try {
                const {
                  data,
                } =
                  await api.post(
                    "/maps/route",
                    {
                      transportsessionId:
                        latest.session
                          .id,

                      costing:
                        "truck",

                      alternatives:
                        0,

                      includeTollEstimate:
                        false,

                      locations:
                        locations.map(
                          (
                            location,
                            index
                          ) => ({
                            id:
                              location.id ||
                              `route-edit-${index}`,

                            type:
                              location.type,

                            sequence:
                              index + 1,

                            lat:
                              location.lat,

                            lon:
                              location.lon,
                          })
                        ),
                    },
                    {
                      signal:
                        controller.signal,
                    }
                  );

                if (
                  controller.signal
                    .aborted ||
                  requestId !==
                    routeEditPreviewRequestIdRef.current
                ) {
                  return;
                }

                const route =
                  normalizeEditableSessionRoute(
                    data
                  );

                if (!route) {
                  throw new Error(
                    "No route returned."
                  );
                }

                setRouteEditPreviewRoute(
                  route
                );

                setRouteEditError(
                  null
                );
              } catch (
                routeError
              ) {
                if (
                  routeError?.name ===
                    "CanceledError" ||
                  routeError?.name ===
                    "AbortError" ||
                  controller.signal
                    .aborted
                ) {
                  return;
                }

                console.warn(
                  "[DriverRoutePlannerMap] live route preview failed:",
                  routeError
                );

                setRouteEditError(
                  routeError
                    ?.response
                    ?.data
                    ?.error ||
                  "Não foi possível recalcular a rota."
                );
              }
            },
            delay
          );
      },
      []
    );

  useEffect(() => {
    return () => {
      clearRouteEditPreviewRequest();

      try {
        routeEditPreviewMarkerRef
          .current
          ?.remove();
      } catch {}

      routeEditPreviewMarkerRef.current =
        null;

      routeEditDragStateRef.current =
        null;
    };
  }, [
    clearRouteEditPreviewRequest,
  ]);

  const selectedDriverTimelinePoints =
    useMemo(() => {
      const rows =
        getDriverAssignedTimelineRows(
          selectedDriver,
          selectedDriverSessions
        );

      rows.sort(
        (
          first,
          second
        ) => {
          const firstDateTime =
            getPointChronologicalValue(
              first.point
            );

          const secondDateTime =
            getPointChronologicalValue(
              second.point
            );

          if (
            firstDateTime !==
            secondDateTime
          ) {
            return firstDateTime <
              secondDateTime
              ? -1
              : 1;
          }

          const firstSessionNumber =
            Number(
              first.session
                ?.transportSessionNumber ??
              first.session
                ?.id ??
              0
            );

          const secondSessionNumber =
            Number(
              second.session
                ?.transportSessionNumber ??
              second.session
                ?.id ??
              0
            );

          if (
            firstSessionNumber !==
            secondSessionNumber
          ) {
            return (
              firstSessionNumber -
              secondSessionNumber
            );
          }

          return (
            Number(
              first.point
                ?.sequence ||
              0
            ) -
            Number(
              second.point
                ?.sequence ||
              0
            )
          );
        }
      );

      return rows.map(
        (
          row,
          index
        ) => ({
          ...row,

          point: {
            ...row.point,

            driverSequence:
              index + 1,
          },
        })
      );
    }, [
      selectedDriver,
      selectedDriverSessions,
    ]);

  // Navegação por sessão, igual à vista dos Trucks.
  const panelDriverSessions = selectedDriverSessions.filter(
    (session) => !session.isHistoricalAnchor
  );

  const panelDriverSession =
    panelDriverSessions.find(
      (session) =>
        String(session.id) === String(driverPanelSessionId)
    ) || panelDriverSessions[0] || null;

  const panelDriverRows = selectedDriverTimelinePoints.filter(
    ({ session }) =>
      String(session.id) === String(panelDriverSession?.id)
  );

  const visibleDriverRows =
    driverPanelMode === "simple"
      ? panelDriverRows
      : selectedDriverTimelinePoints;

  const panelCompleted = panelDriverRows.filter(
    ({ point }) => isPointCompleted(point)
  ).length;

  const panelPercent = panelDriverRows.length
    ? Math.round(
        (panelCompleted / panelDriverRows.length) * 100
      )
    : 0;

  const selectedDriverSessionDetails =
    useMemo(() => {
      const sessionsMap =
        new Map();

      selectedDriverTimelinePoints.forEach(
        (
          row
        ) => {
          const key =
            String(
              row.session.id
            );

          if (
            !sessionsMap.has(
              key
            )
          ) {
            sessionsMap.set(
              key,
              {
                session:
                  row.session,

                points:
                  [],
              }
            );
          }

          sessionsMap
            .get(
              key
            )
            .points
            .push(
              row.point
            );
        }
      );

      return Array.from(
        sessionsMap.values()
      ).map(
        (
          item
        ) => {
          const points =
            item.points;

          const pickupCount =
            points.filter(
              (
                point
              ) =>
                String(
                  point?.type ||
                  ""
                )
                  .trim()
                  .toUpperCase() ===
                "PICKUP"
            ).length;

          const deliveryCount =
            points.filter(
              (
                point
              ) =>
                String(
                  point?.type ||
                  ""
                )
                  .trim()
                  .toUpperCase() ===
                "DELIVERY"
            ).length;

          const strategicPointCount =
            points.filter(
              (
                point
              ) =>
                String(
                  point?.type ||
                  ""
                )
                  .trim()
                  .toUpperCase() ===
                "STRATEGICPOINT"
            ).length;

          const fuelCount =
            points.filter(
              (
                point
              ) =>
                String(
                  point?.type ||
                  ""
                )
                  .trim()
                  .toUpperCase() ===
                "FUEL"
            ).length;

          return {
            session:
              item.session,

            points,
            pickupCount,
            deliveryCount,
            strategicPointCount,
            fuelCount,
          };
        }
      );
    }, [
      selectedDriverTimelinePoints,
    ]);

  const driverProgressByKey =
    useMemo(() => {
      const progressMap =
        new Map();

      scopedDrivers.forEach(
        (
          driver
        ) => {
          const points =
            getDriverAssignedTimelineRows(
              driver,
              currentSessions
            ).map(
              (
                row
              ) =>
                row.point
            );

          const totalPoints =
            points.length;

          const finishedPoints =
            points.filter(
              isPointFinished
            ).length;

          const percentage =
            totalPoints > 0
              ? Math.round(
                  (
                    finishedPoints /
                    totalPoints
                  ) *
                    100
                )
              : 0;

          progressMap.set(
            driver.driverKey,
            {
              totalPoints,

              finishedPoints,

              percentage,
            }
          );
        }
      );

      return progressMap;
    }, [
      currentSessions,
      scopedDrivers,
    ]);

  useEffect(() => {
    const connections =
      selectedDriver
        ?.connections ||
      [];

    if (
      !connections.length
    ) {
      setSelectedConnectionId(
        null
      );

      return;
    }

    setSelectedConnectionId(
      (
        current
      ) => {
        const exists =
          connections.some(
            (
              connection
            ) =>
              String(
                connection.id
              ) ===
              String(
                current
              )
          );

        if (exists) {
          return current;
        }

        return null;

      }
    );
  }, [
    selectedDriver,
  ]);

  const selectedConnection =
    useMemo(() => {
      return (
        selectedDriver
          ?.connections
          ?.find(
            (
              connection
            ) =>
              String(
                connection.id
              ) ===
              String(
                selectedConnectionId
              )
          ) ||
        null
      );
    }, [
      selectedConnectionId,
      selectedDriver,
    ]);

  const patchConnection =
    useCallback(
      (
        driverKey,
        connectionId,
        expectedSignature,
        patch
      ) => {
        setDrivers(
          (
            currentDrivers
          ) =>
            currentDrivers.map(
              (
                driver
              ) => {
                if (
                  driver.driverKey !==
                  driverKey
                ) {
                  return driver;
                }

                return {
                  ...driver,

                  connections:
                    (
                      driver.connections ||
                      []
                    ).map(
                      (
                        connection
                      ) => {
                        if (
                          String(
                            connection.id
                          ) !==
                          String(
                            connectionId
                          )
                        ) {
                          return connection;
                        }

                        if (
                          getConnectionSignature(
                            connection
                          ) !==
                          expectedSignature
                        ) {
                          return connection;
                        }

                        return {
                          ...connection,
                          ...patch,
                        };
                      }
                    ),
                };
              }
            )
        );
      },
      []
    );

  /*
   * Route only the selected driver's connectors.
   *
   * This avoids calculating hundreds of road routes
   * when the user only wants to inspect one driver.
   */
  useEffect(() => {
    if (
      !selectedDriver
    ) {
      return;
    }

    const candidates =
      (
        selectedDriver.connections ||
        []
      ).filter(
        (
          connection
        ) =>
          connection.routable &&
          !connection.route &&
          !connection.routeError
      );

    if (
      !candidates.length
    ) {
      return;
    }

    const queue =
      [...candidates];

    const routeConnection =
      async (
        connection
      ) => {
        const signature =
          getConnectionSignature(
            connection
          );

        const requestKey =
          `${selectedDriver.driverKey}:${connection.id}:${signature}`;

        if (
          routingInFlightRef.current.has(
            requestKey
          )
        ) {
          return;
        }

        routingInFlightRef.current.add(
          requestKey
        );

        patchConnection(
          selectedDriver.driverKey,
          connection.id,
          signature,
          {
            routing:
              true,
          }
        );

        try {
          if (
            samePoint(
              connection.fromPoint,
              connection.toPoint
            )
          ) {
            patchConnection(
              selectedDriver.driverKey,
              connection.id,
              signature,
              {
                routing:
                  false,

                route:
                  createZeroDistanceRoute(
                    connection
                  ),
              }
            );

            return;
          }

          const {
            data,
          } =
            await api.post(
              "/maps/route",
              {
                costing:
                  "truck",

                alternatives:
                  0,

                includeTollEstimate:
                  false,

                locations: [
                  {
                    id:
                      connection
                        .fromPoint
                        .id,

                    type:
                      "DRIVER_CONNECTION_FROM",

                    sequence:
                      1,

                    lat:
                      connection
                        .fromPoint
                        .lat,

                    lon:
                      connection
                        .fromPoint
                        .lng,
                  },
                  {
                    id:
                      connection
                        .toPoint
                        .id,

                    type:
                      "DRIVER_CONNECTION_TO",

                    sequence:
                      2,

                    lat:
                      connection
                        .toPoint
                        .lat,

                    lon:
                      connection
                        .toPoint
                        .lng,
                  },
                ],
              }
            );

          const route =
            normalizeRouteResponse(
              data
            );

          if (!route) {
            throw new Error(
              "No route returned."
            );
          }

          patchConnection(
            selectedDriver.driverKey,
            connection.id,
            signature,
            {
              routing:
                false,

              route,

              routeError:
                null,
            }
          );
        } catch (
          routeError
        ) {
          console.warn(
            "[DriverRoutePlannerMap] connector route failed:",
            routeError?.message
          );

          patchConnection(
            selectedDriver.driverKey,
            connection.id,
            signature,
            {
              routing:
                false,

              routeError:
                true,
            }
          );
        } finally {
          routingInFlightRef.current.delete(
            requestKey
          );
        }
      };

    const workerCount =
      Math.min(
        4,
        queue.length
      );

    Array.from({
      length:
        workerCount,
    }).forEach(
      async () => {
        while (
          queue.length
        ) {
          const connection =
            queue.shift();

          if (
            connection
          ) {
            await routeConnection(
              connection
            );
          }
        }
      }
    );
  }, [
    patchConnection,
    selectedDriver,
  ]);

  const sessionIdsKey =
    useMemo(() => {
      const ids =
        new Set();

      scopedDrivers.forEach(
        (
          driver
        ) => {
          (
            driver.sessions ||
            []
          ).forEach(
            (
              session
            ) => {
              const id =
                Number(
                  session.id
                );

              if (
                Number.isInteger(
                  id
                ) &&
                id > 0
              ) {
                ids.add(
                  id
                );
              }
            }
          );
        }
      );

      return Array.from(
        ids
      )
        .sort(
          (
            first,
            second
          ) =>
            first -
            second
        )
        .join(
          ","
        );
    }, [
      scopedDrivers,
    ]);

  /*
   * WebSocket is the source of freshness.
   * Cache only supplies the instant first paint.
   */
  useEffect(() => {
    const socket =
      getSocket();

    let refreshTimer =
      null;

    const sessionIds =
      sessionIdsKey
        ? sessionIdsKey
            .split(",")
            .map(
              Number
            )
            .filter(
              Number.isInteger
            )
        : [];

    const scheduleRefresh =
      () => {
        if (
          refreshTimer
        ) {
          window.clearTimeout(
            refreshTimer
          );
        }

        refreshTimer =
          window.setTimeout(
            () => {
              refreshPlanner({
                silent:
                  true,
              });
            },
            140
          );
      };

    const subscribe =
      () => {
        socket.emit(
          "subscribe:loads"
        );

        if (
          sessionIds.length
        ) {
          socket.emit(
            "subscribe:sessions",
            {
              ids:
                sessionIds,
            }
          );
        }
      };

    const handleConnect =
      () => {
        subscribe();

        scheduleRefresh();
      };

    const relevantEvents = [
      "loads:rev",
      "loads:stale",
      "load:updated",
      "load:points:stale",
      "pd:updated",
      "pd:created",
      "pd:deleted",
      "pd:bulk-driver",
      "session:points:stale",
      "session:route:updated",
    ];

    subscribe();

    socket.on(
      "connect",
      handleConnect
    );

    socket.on(
      "reconnect",
      handleConnect
    );

    relevantEvents.forEach(
      (
        eventName
      ) => {
        socket.on(
          eventName,
          scheduleRefresh
        );
      }
    );

    const handleOnline =
      () => {
        scheduleRefresh();
      };

    const handleVisibilityChange =
      () => {
        if (
          document.visibilityState ===
          "visible"
        ) {
          scheduleRefresh();
        }
      };

    window.addEventListener(
      "online",
      handleOnline
    );

    document.addEventListener(
      "visibilitychange",
      handleVisibilityChange
    );

    return () => {
      if (
        refreshTimer
      ) {
        window.clearTimeout(
          refreshTimer
        );
      }

      socket.off(
        "connect",
        handleConnect
      );

      socket.off(
        "reconnect",
        handleConnect
      );

      relevantEvents.forEach(
        (
          eventName
        ) => {
          socket.off(
            eventName,
            scheduleRefresh
          );
        }
      );

      if (
        sessionIds.length
      ) {
        socket.emit(
          "unsubscribe:sessions",
          {
            ids:
              sessionIds,
          }
        );
      }

      window.removeEventListener(
        "online",
        handleOnline
      );

      document.removeEventListener(
        "visibilitychange",
        handleVisibilityChange
      );
    };
  }, [
    refreshPlanner,
    sessionIdsKey,
  ]);

  useEffect(() => {
    if (
      !mapContainerRef.current
    ) {
      return undefined;
    }

    let resizeObserver =
      null;

    const map =
      new maplibregl.Map({
        container:
          mapContainerRef.current,

        style:
          MAP_STYLE,

        center:
          DEFAULT_CENTER,

        zoom:
          DEFAULT_ZOOM,

        attributionControl:
          true,
      });

    mapRef.current =
      map;

    map.addControl(
      new maplibregl.NavigationControl(),
      "top-right"
    );

    const handleLoad =
      () => {
        map.addSource(
          SESSION_ROUTES_SOURCE_ID,
          {
            type:
              "geojson",

            data: {
              type:
                "FeatureCollection",

              features: [],
            },
          }
        );

        // Brilho exterior do percurso ainda por fazer.
        map.addLayer({
          id: "driver-session-routes-neon",
          type: "line",
          source: SESSION_ROUTES_SOURCE_ID,

          filter: [
            "all",
            ["==", ["get", "historical"], false],
            ["==", ["get", "completed"], false],
          ],

          layout: {
            "line-cap": "round",
            "line-join": "round",
          },

          paint: {
            "line-color": "#1769FF",
            "line-width": 22,
            "line-blur": 6,
            "line-opacity": 0.8,
          },
        });

        map.addLayer({
          id:
            SESSION_ROUTES_CASING_LAYER_ID,


          type:
            "line",

          source:
            SESSION_ROUTES_SOURCE_ID,

          layout: {
            "line-cap":
              "round",

            "line-join":
              "round",
          },

          paint: {
            "line-color": [
              "case",
              [
                "any",
                ["boolean", ["get", "historical"], false],
                ["boolean", ["get", "completed"], false],
              ],
              "#0B2B66",
              "#278CFF",
            ],

            "line-width": 11,
            "line-opacity": 1,
          },

        });

        /*
         * Historical/completed Transport Session.
         *
         * This is deliberately wider than the current route.
         * When both routes use the same road, the current route
         * will sit on top and leave a green edge visible.
         */
        map.addLayer({
          id:
            SESSION_ROUTES_HISTORICAL_LAYER_ID,

          type:
            "line",

          source:
            SESSION_ROUTES_SOURCE_ID,

          filter: [
            "==",
            [
              "get",
              "historical",
            ],
            true,
          ],

          layout: {
            "line-cap":
              "round",

            "line-join":
              "round",
          },

          paint: {
            "line-color": "#16478F",
            "line-width": 8,
            "line-opacity": 1,
          },

        });

        /*
         * Current Transport Sessions are rendered AFTER the
         * historical layer, therefore they always stay on top.
         */
        map.addLayer({
          id:
            SESSION_ROUTES_LINE_LAYER_ID,

          type:
            "line",

          source:
            SESSION_ROUTES_SOURCE_ID,

          filter: [
            "!=",
            [
              "get",
              "historical",
            ],
            true,
          ],

          layout: {
            "line-cap":
              "round",

            "line-join":
              "round",
          },

          paint: {
            "line-color": [
              "case",

              ["boolean", ["get", "completed"], false],
              "#16478F",

              "#278CFF",
            ],

            "line-width": 7,
            "line-opacity": 1,
          },

        });

        // Centro luminoso, igual às Sessões/Trucks.
        map.addLayer({
          id: "driver-session-routes-highlight",
          type: "line",
          source: SESSION_ROUTES_SOURCE_ID,

          filter: [
            "all",
            ["==", ["get", "historical"], false],
            ["==", ["get", "completed"], false],
          ],

          layout: {
            "line-cap": "round",
            "line-join": "round",
          },

          paint: {
            "line-color": "#D9F6FF",
            "line-width": 2,
            "line-opacity": 0.95,
          },
        });

        map.addLayer({
          id:
            SESSION_ROUTES_HITBOX_LAYER_ID,


          type:
            "line",

          source:
            SESSION_ROUTES_SOURCE_ID,

          layout: {
            "line-cap":
              "round",

            "line-join":
              "round",
          },

          paint: {
            "line-color":
              "#000000",

            "line-width":
              24,

            "line-opacity":
              0,
          },
        });

        map.addSource(
          ROUTES_SOURCE_ID,
          {
            type:
              "geojson",

            data: {
              type:
                "FeatureCollection",

              features: [],
            },
          }
        );

        map.addLayer({
          id:
            ROUTES_CASING_LAYER_ID,

          type:
            "line",

          source:
            ROUTES_SOURCE_ID,

          layout: {
            "line-cap":
              "round",

            "line-join":
              "round",
          },

          paint: {
            "line-color":
              "#ffffff",

            "line-width": [
              "case",

              [
                "boolean",
                [
                  "get",
                  "selected",
                ],
                false,
              ],

              7,
              5.5,
            ],

            "line-opacity":
              0.92,
          },
        });

        map.addLayer({
          id:
            ROUTES_LINE_LAYER_ID,

          type:
            "line",

          source:
            ROUTES_SOURCE_ID,

          layout: {
            "line-cap":
              "round",

            "line-join":
              "round",
          },

          paint: {
            "line-color": [
              "case",

              ["==", ["get", "status"], "CONFLICT"],
              "#DC2626",

              ["boolean", ["get", "completed"], false],
              "#16478F",

              ["boolean", ["get", "selected"], false],
              "#278CFF",

              ["boolean", ["get", "routed"], false],
              "#278CFF",

              "#64748B",
            ],


            "line-width": [
              "case",

              [
                "boolean",
                [
                  "get",
                  "selected",
                ],
                false,
              ],

              5,
              3.5,
            ],

            "line-opacity":
              0.95,

          },
        });

        map.addLayer({
          id:
            ROUTES_HITBOX_LAYER_ID,

          type:
            "line",

          source:
            ROUTES_SOURCE_ID,

          paint: {
            "line-color":
              "#000000",

            "line-width":
              24,

            "line-opacity":
              0,
          },
        });

        // As ligações entre sessões ficam por baixo
        // das rotas operacionais do motorista.
        [
          ROUTES_CASING_LAYER_ID,
          ROUTES_LINE_LAYER_ID,
          ROUTES_HITBOX_LAYER_ID,
        ].forEach((layerId) => {
          map.moveLayer(
            layerId,
            "driver-session-routes-neon"
          );
        });

        try {
          ensureRoadReferenceOverlay(
            map
          );

        } catch (
          roadReferenceError
        ) {
          console.warn(
            "[DriverRoutePlannerMap] Could not initialise road references:",
            roadReferenceError
          );
        }

        map.addSource(
          POINTS_SOURCE_ID,
          {
            type:
              "geojson",

            data: {
              type:
                "FeatureCollection",

              features: [],
            },
          }
        );

        map.addLayer({
          id:
            POINTS_HALO_LAYER_ID,

          type:
            "circle",

          source:
            POINTS_SOURCE_ID,

          paint: {
            "circle-radius":
              10,

            "circle-color":
              "#ffffff",

            "circle-opacity":
              0.95,
          },
        });

        map.addLayer({
          id:
            POINTS_CIRCLE_LAYER_ID,

          type:
            "circle",

          source:
            POINTS_SOURCE_ID,

          paint: {
            "circle-radius":
              7,

            "circle-color": [
              "case",

              [
                "==",
                [
                  "get",
                  "role",
                ],
                "FROM",
              ],

              "#2563eb",

              [
                "==",
                [
                  "get",
                  "role",
                ],
                "TO",
              ],

              "#0f172a",

              [
                "match",

                [
                  "get",
                  "type",
                ],

                "PICKUP",
                "#0b8dbd",

                "ORIGIN",
                "#0b8dbd",

                "DELIVERY",
                "#111827",

                "DESTINATION",
                "#111827",

                "FUEL",
                "#f59e0b",

                "#9333ea",
              ],
            ],

            "circle-stroke-color":
              "#ffffff",

            "circle-stroke-width":
              1.5,
          },
        });

        map.addLayer({
          id:
            POINTS_LABEL_LAYER_ID,

          type:
            "symbol",

          source:
            POINTS_SOURCE_ID,

          layout: {
            "text-field": [
              "get",
              "label",
            ],

            "text-size":
              10,

            "text-offset": [
              0,
              -1.6,
            ],

            "text-allow-overlap":
              false,
          },

          paint: {
            "text-color":
              "#0f172a",

            "text-halo-color":
              "#ffffff",

            "text-halo-width":
              2,
          },
        });

        // Ocultar os antigos círculos e etiquetas.
        // Os pins gráficos continuam visíveis.
        [
          POINTS_HALO_LAYER_ID,
          POINTS_CIRCLE_LAYER_ID,
          POINTS_LABEL_LAYER_ID,
        ].forEach((layerId) => {
          map.setLayoutProperty(
            layerId,
            "visibility",
            "none"
          );
        });

        map.on(
          "click",
          ROUTES_HITBOX_LAYER_ID,

          (
            event
          ) => {
            const connectionId =
              event
                ?.features?.[0]
                ?.properties
                ?.connectionId;

            if (
              connectionId
            ) {
              setSelectedConnectionId(
                connectionId
              );
            }
          }
        );

        map.on(
          "mouseenter",
          ROUTES_HITBOX_LAYER_ID,
          () => {
            map.getCanvas().style.cursor =
              "pointer";
          }
        );

        map.on(
          "mouseleave",
          ROUTES_HITBOX_LAYER_ID,
          () => {
            map.getCanvas().style.cursor =
              "";
          }
        );

        setMapReady(
          true
        );
      };

    map.on(
      "load",
      handleLoad
    );

    if (
      typeof ResizeObserver !==
      "undefined"
    ) {
      resizeObserver =
        new ResizeObserver(
          () => {
            try {
              map.resize();
            } catch {}
          }
        );

      resizeObserver.observe(
        mapContainerRef.current
      );
    }

    return () => {
      setMapReady(
        false
      );

      resizeObserver?.disconnect();

      try {
        map.remove();
      } catch {}

      if (
        mapRef.current ===
        map
      ) {
        mapRef.current =
          null;
      }
    };
  }, []);

  useEffect(() => {
    if (
      !mapReady ||
      !mapRef.current ||
      !routeEditMode ||
      !mapRef.current.getLayer(
        SESSION_ROUTES_HITBOX_LAYER_ID
      )
    ) {
      return undefined;
    }

    const map =
      mapRef.current;

    const isMarkerEvent =
      (
        event
      ) => {
        const target =
          event
            ?.originalEvent
            ?.target;

        if (
          !target ||
          typeof target.closest !==
            "function"
        ) {
          return false;
        }

        return Boolean(
          target.closest(
            ".maplibregl-marker"
          )
        );
      };

    const getRouteFeature =
      (
        event
      ) => {
        if (
          !event?.point ||
          isMarkerEvent(
            event
          )
        ) {
          return null;
        }

        try {
          const features =
            map.queryRenderedFeatures(
              event.point,
              {
                layers: [
                  SESSION_ROUTES_HITBOX_LAYER_ID,
                ],
              }
            );

          return (
            features[0] ||
            null
          );
        } catch {
          return null;
        }
      };

    const removePreviewMarker =
      () => {
        try {
          routeEditPreviewMarkerRef
            .current
            ?.remove();
        } catch {}

        routeEditPreviewMarkerRef.current =
          null;
      };

    const restoreMapInteraction =
      () => {
        try {
          if (
            !map.dragPan.isEnabled()
          ) {
            map.dragPan.enable();
          }

          map.getCanvas().style.cursor =
            "";
        } catch {}
      };

    const handleMouseDown =
      (
        event
      ) => {
        if (
          event
            ?.originalEvent
            ?.button !==
            undefined &&
          event
            .originalEvent
            .button !==
            0
        ) {
          return;
        }

        if (
          isMarkerEvent(
            event
          )
        ) {
          return;
        }

        const feature =
          getRouteFeature(
            event
          );

        if (
          !feature ||
          !event?.lngLat
        ) {
          return;
        }

        const sessionId =
          feature
            ?.properties
            ?.sessionId;

        if (!sessionId) {
          return;
        }

        const session =
          selectedDriverSessions.find(
            (
              item
            ) =>
              String(
                item.id
              ) ===
              String(
                sessionId
              )
          );

        if (
          !session ||
          session
            .isHistoricalAnchor
        ) {
          return;
        }

        const points =
          getEditableSessionPoints(
            session
          );

        if (
          points.length < 2
        ) {
          setRouteEditError(
            "Esta sessão precisa de pelo menos dois pontos com coordenadas."
          );

          return;
        }

        const isSameSession =
          String(
            routeEditSessionId
          ) ===
          String(
            session.id
          );

        const baseViaPoints =
          isSameSession
            ? routeEditViaPoints
            : getSavedSessionViaPoints(
                session
                  .routeSnapshot
              );

        if (
          points.length +
            baseViaPoints.length >=
          25
        ) {
          setRouteEditError(
            "Esta rota já atingiu o limite de pontos do routing."
          );

          return;
        }

        const segmentIndex =
          getNearestSessionSegmentIndex({
            map,
            points,

            lngLat:
              event.lngLat,
          });

        if (
          segmentIndex < 0
        ) {
          return;
        }

        event.preventDefault?.();

        setRouteEditSessionId(
          session.id
        );

        setRouteEditError(
          null
        );

        routeEditDragStateRef.current = {
          session,

          segmentIndex,

          baseViaPoints,

          lat:
            Number(
              event.lngLat.lat
            ),

          lng:
            Number(
              event.lngLat.lng
            ),

          startX:
            Number(
              event.point?.x
            ),

          startY:
            Number(
              event.point?.y
            ),

          moved:
            false,
        };

        removePreviewMarker();

        const previewElement =
          createRouteEditViaPreviewElement();

        routeEditPreviewMarkerRef.current =
          new maplibregl.Marker({
            element:
              previewElement,

            anchor:
              "center",
          })
            .setLngLat([
              event.lngLat.lng,
              event.lngLat.lat,
            ])
            .addTo(
              map
            );

        try {
          map.dragPan.disable();

          map.getCanvas().style.cursor =
            "grabbing";
        } catch {}
      };

    const handleMouseMove =
      (
        event
      ) => {
        const dragState =
          routeEditDragStateRef.current;

        if (!dragState) {
          if (
            isMarkerEvent(
              event
            )
          ) {
            map.getCanvas().style.cursor =
              "";

            return;
          }

          map.getCanvas().style.cursor =
            getRouteFeature(
              event
            )
              ? "grab"
              : "";

          return;
        }

        if (
          !event?.lngLat
        ) {
          return;
        }

        dragState.lat =
          Number(
            event.lngLat.lat
          );

        dragState.lng =
          Number(
            event.lngLat.lng
          );

        const distance =
          Math.hypot(
            Number(
              event.point?.x
            ) -
              dragState.startX,
            Number(
              event.point?.y
            ) -
              dragState.startY
          );

        if (
          distance > 3
        ) {
          dragState.moved =
            true;
        }

        routeEditPreviewMarkerRef
          .current
          ?.setLngLat([
            dragState.lng,
            dragState.lat,
          ]);

        if (
          dragState.moved
        ) {
          const previewViaPoint = {
            id:
              "__driver-route-live-via__",

            lat:
              dragState.lat,

            lng:
              dragState.lng,

            segmentIndex:
              dragState.segmentIndex,

            order:
              dragState
                .baseViaPoints
                .length +
              1,
          };

          queueRouteEditPreview(
            dragState.session,
            [
              ...dragState
                .baseViaPoints,

              previewViaPoint,
            ]
          );
        }

        event.preventDefault?.();
      };

    const finishRouteDragging =
      (
        event = null
      ) => {
        const dragState =
          routeEditDragStateRef.current;

        if (!dragState) {
          return;
        }

        if (
          event?.lngLat
        ) {
          dragState.lat =
            Number(
              event.lngLat.lat
            );

          dragState.lng =
            Number(
              event.lngLat.lng
            );
        }

        routeEditDragStateRef.current =
          null;

        removePreviewMarker();

        restoreMapInteraction();

        if (
          !dragState.moved
        ) {
          return;
        }

        const newViaPoint = {
          id:
            `planner-via-${Date.now()}-${Math.random()
              .toString(36)
              .slice(2, 7)}`,

          lat:
            dragState.lat,

          lng:
            dragState.lng,

          segmentIndex:
            dragState.segmentIndex,

          order:
            dragState
              .baseViaPoints
              .length +
            1,
        };

        const nextViaPoints = [
          ...dragState
            .baseViaPoints,

          newViaPoint,
        ];

        setRouteEditSessionId(
          dragState
            .session
            .id
        );

        setRouteEditViaPoints(
          nextViaPoints
        );

        queueRouteEditPreview(
          dragState.session,
          nextViaPoints
        );
      };

    const handleMouseUp =
      (
        event
      ) => {
        finishRouteDragging(
          event
        );
      };

    const handleWindowMouseUp =
      () => {
        finishRouteDragging();
      };

    map.on(
      "mousedown",
      handleMouseDown
    );

    map.on(
      "mousemove",
      handleMouseMove
    );

    map.on(
      "mouseup",
      handleMouseUp
    );

    window.addEventListener(
      "mouseup",
      handleWindowMouseUp
    );

    return () => {
      routeEditDragStateRef.current =
        null;

      removePreviewMarker();

      try {
        map.off(
          "mousedown",
          handleMouseDown
        );

        map.off(
          "mousemove",
          handleMouseMove
        );

        map.off(
          "mouseup",
          handleMouseUp
        );
      } catch {}

      window.removeEventListener(
        "mouseup",
        handleWindowMouseUp
      );

      restoreMapInteraction();
    };
  }, [
    mapReady,
    queueRouteEditPreview,
    routeEditMode,
    routeEditSessionId,
    routeEditViaPoints,
    selectedDriverSessions,
  ]);

  useEffect(() => {
    if (
      !mapReady ||
      !mapRef.current
    ) {
      return;
    }

    const sessionRoutesSource =
      mapRef.current.getSource(
        SESSION_ROUTES_SOURCE_ID
      );

    const routesSource =
      mapRef.current.getSource(
        ROUTES_SOURCE_ID
      );

    const pointsSource =
      mapRef.current.getSource(
        POINTS_SOURCE_ID
      );

    sessionRoutesSource?.setData?.(
      buildSessionRoutesFeatureCollection(
        selectedDriverSessionDetails,
        routeEditSessionId,
        routeEditPreviewRoute
      )
    );

    routesSource?.setData?.(
      buildRoutesFeatureCollection(
        selectedDriver,
        selectedConnectionId
      )
    );

    pointsSource?.setData?.(
      buildPointsFeatureCollection(
        selectedDriver
      )
    );
  }, [
    mapReady,
    routeEditPreviewRoute,
    routeEditSessionId,
    selectedConnectionId,
    selectedDriver,
    selectedDriverSessionDetails,
  ]);

  useEffect(() => {
    pointMarkersRef.current.forEach(
      (
        marker
      ) => {
        marker.remove();
      }
    );

    pointMarkersRef.current =
      [];

    if (
      !mapReady ||
      !mapRef.current ||
      !selectedDriver
    ) {
      return undefined;
    }

    const nextMarkers =
      [];

    selectedDriverSessionDetails.forEach(
      (
        {
          session,
          points,
        }
      ) => {
        points.forEach(
          (
            point,
            index
          ) => {
            const lat =
              Number(
                point?.lat
              );

            const lng =
              Number(
                point?.lng
              );

            if (
              !Number.isFinite(
                lat
              ) ||
              !Number.isFinite(
                lng
              ) ||
              Math.abs(
                lat
              ) > 90 ||
              Math.abs(
                lng
              ) > 180
            ) {
              return;
            }

            const pointKey =
              [
                session.id,
                point?.id ??
                  index,
              ].join(
                ":"
              );

            const element =
              createDriverPointMarkerElement(
                point,
                index,
                {
                  initiallyCollapsed:
                    !expandedPointMarkersRef
                      .current
                      .has(
                        pointKey
                      ),

                  onCollapsedChange:
                    (
                      isCollapsed
                    ) => {
                      if (
                        isCollapsed
                      ) {
                        expandedPointMarkersRef
                          .current
                          .delete(
                            pointKey
                          );

                        return;
                      }

                      expandedPointMarkersRef
                        .current
                        .add(
                          pointKey
                        );
                    },
                }
              );

            const marker =
              new maplibregl.Marker({
                element,

                anchor:
                  "bottom",

                offset: [
                  0,
                  -4,
                ],
              })
                .setLngLat([
                  lng,
                  lat,
                ])
                .addTo(
                  mapRef.current
                );

            nextMarkers.push(
              marker
            );
          }
        );
      }
    );

    pointMarkersRef.current =
      nextMarkers;

    return () => {
      nextMarkers.forEach(
        (
          marker
        ) => {
          marker.remove();
        }
      );

      if (
        pointMarkersRef.current ===
        nextMarkers
      ) {
        pointMarkersRef.current =
          [];
      }
    };
  }, [
    mapReady,
    selectedDriver,
    selectedDriverSessionDetails,
  ]);

  useEffect(() => {
    routeEditMarkersRef
      .current
      .forEach(
        (
          marker
        ) => {
          marker.remove();
        }
      );

    routeEditMarkersRef.current =
      [];

    if (
      !mapReady ||
      !mapRef.current ||
      !routeEditMode ||
      !routeEditSession
    ) {
      return undefined;
    }

    const map =
      mapRef.current;

    const markers =
      [];

    routeEditViaPoints.forEach(
      (
        viaPoint,
        index
      ) => {
        const element =
          document.createElement(
            "button"
          );

        element.type =
          "button";

        element.className =
          "driver-route-planner__route-via-marker";

        element.innerHTML =
          "<span></span>";

        element.title =
          "Arrasta para ajustar. Duplo clique para remover.";

        element.addEventListener(
          "dblclick",
          (
            event
          ) => {
            event.preventDefault();
            event.stopPropagation();

            setRouteEditViaPoints(
              (
                current
              ) =>
                current.filter(
                  (
                    item
                  ) =>
                    String(
                      item.id
                    ) !==
                    String(
                      viaPoint.id
                    )
                )
            );
          }
        );

        const marker =
          new maplibregl.Marker({
            element,

            anchor:
              "center",

            draggable:
              true,
          })
            .setLngLat([
              viaPoint.lng,
              viaPoint.lat,
            ])
            .addTo(
              map
            );

        const buildNextViaPoints =
          () => {
            const lngLat =
              marker.getLngLat();

            return routeEditViaPoints.map(
              (
                item
              ) =>
                String(
                  item.id
                ) ===
                String(
                  viaPoint.id
                )
                  ? {
                      ...item,

                      lat:
                        lngLat.lat,

                      lng:
                        lngLat.lng,
                    }
                  : item
            );
          };

        marker.on(
          "dragstart",
          () => {
            try {
              map.dragPan.disable();
            } catch {}

            queueRouteEditPreview(
              routeEditSession,
              buildNextViaPoints()
            );
          }
        );

        marker.on(
          "drag",
          () => {
            queueRouteEditPreview(
              routeEditSession,
              buildNextViaPoints()
            );
          }
        );

        marker.on(
          "dragend",
          () => {
            const nextViaPoints =
              buildNextViaPoints();

            setRouteEditViaPoints(
              nextViaPoints
            );

            queueRouteEditPreview(
              routeEditSession,
              nextViaPoints
            );

            window.setTimeout(
              () => {
                try {
                  map.dragPan.enable();
                } catch {}
              },
              0
            );
          }
        );

        markers.push(
          marker
        );
      }
    );

    routeEditMarkersRef.current =
      markers;

    return () => {
      markers.forEach(
        (
          marker
        ) => {
          marker.remove();
        }
      );

      routeEditMarkersRef.current =
        [];
    };
  }, [
    mapReady,
    queueRouteEditPreview,
    routeEditMode,
    routeEditSession,
    routeEditViaPoints,
  ]);

  const filteredDrivers =
    useMemo(() => {
      const search =
        searchTerm
          .trim()
          .toLowerCase();

      if (!search) {
        return scopedDrivers;
      }

      return scopedDrivers.filter(
        (
          driver
        ) => {
          const values = [
            driver.driverName,

            ...(
              driver.sessions ||
              []
            ).flatMap(
              (
                session
              ) => [
                getSessionLabel(
                  session
                    .transportSessionNumber,
                  session.id
                ),

                session.truckPlate,

                getPointName(
                  session.startPoint
                ),

                getPointName(
                  session.endPoint
                ),
              ]
            ),
          ];

          return values.some(
            (
              value
            ) =>
              String(
                value ||
                ""
              )
                .toLowerCase()
                .includes(
                  search
                )
          );
        }
      );
    }, [
      scopedDrivers,
      searchTerm,
    ]);

  const handleSaveRouteEdit =
    useCallback(
      async () => {
        if (
          !routeEditSession ||
          !routeEditPreviewRoute
        ) {
          return;
        }

        const locations =
          buildEditableRoutingLocations({
            session:
              routeEditSession,

            viaPoints:
              routeEditViaPoints,
          });

        if (
          locations.length < 2 ||
          locations.length > 25
        ) {
          return;
        }

        setRouteEditSaving(
          true
        );

        setRouteEditError(
          null
        );

        try {
          const {
            data:
              sessionData,
          } =
            await api.get(
              `/transportsessions/${routeEditSession.id}`
            );

          const transportSession =
            sessionData
              ?.transportSession ??
            sessionData;

          const snapshot =
            parseRouteSnapshotObject(
              transportSession
                ?.routeSnapshot
            ) ||
            {};

          const existingRoute =
            snapshot
              ?.route ||
            {};

          await api.put(
            `/transportsessions/${routeEditSession.id}/route`,
            {
              name:
                snapshot
                  ?.name ||
                `Rota da sessão ${routeEditSession.id}`,

              description:
                snapshot
                  ?.description ||
                null,

              routeNumber:
                snapshot
                  ?.routeNumber ||
                null,

              vehicleProfile:
                snapshot
                  ?.vehicleProfile ||
                "truck",

              settings: {
                avoidTolls:
                  Boolean(
                    snapshot
                      ?.settings
                      ?.avoidTolls ??
                    snapshot
                      ?.avoidTolls
                  ),

                avoidFerries:
                  Boolean(
                    snapshot
                      ?.settings
                      ?.avoidFerries ??
                    snapshot
                      ?.avoidFerries
                  ),

                avoidHighways:
                  Boolean(
                    snapshot
                      ?.settings
                      ?.avoidHighways ??
                    snapshot
                      ?.avoidHighways
                  ),
              },

              points:
                buildEditableSnapshotPoints(
                  locations
                ),

              route: {
                ...existingRoute,

                distanceKm:
                  routeEditPreviewRoute
                    .distanceKm ??
                  null,

                durationSeconds:
                  routeEditPreviewRoute
                    .durationSeconds ??
                  null,

                durationNoTrafficSeconds:
                  routeEditPreviewRoute
                    .durationNoTrafficSeconds ??
                  routeEditPreviewRoute
                    .durationSeconds ??
                  null,

                routeName:
                  routeEditPreviewRoute
                    .routeName ??
                  null,

                roadNames:
                  Array.isArray(
                    routeEditPreviewRoute
                      .roadNames
                  )
                    ? routeEditPreviewRoute
                        .roadNames
                    : [],

                hasTolls:
                  Boolean(
                    routeEditPreviewRoute
                      .hasTolls
                  ),

                tollText:
                  routeEditPreviewRoute
                    .tollText ??
                  null,

                tollDistanceKm:
                  routeEditPreviewRoute
                    .tollDistanceKm ??
                  null,

                tollRoadNames:
                  Array.isArray(
                    routeEditPreviewRoute
                      .tollRoadNames
                  )
                    ? routeEditPreviewRoute
                        .tollRoadNames
                    : [],

                tollAnalysisStatus:
                  routeEditPreviewRoute
                    .tollAnalysisStatus ??
                  null,

                tollEstimate:
                  routeEditPreviewRoute
                    .tollEstimate ??
                  null,

                hasHighways:
                  Boolean(
                    routeEditPreviewRoute
                      .hasHighways
                  ),

                geometry:
                  routeEditPreviewRoute
                    .geometry,

                source:
                  routeEditPreviewRoute
                    .source ||
                  "VALHALLA",
              },
            }
          );

          setRouteEditMode(
            false
          );

          setRouteEditSessionId(
            null
          );

          setRouteEditViaPoints(
            []
          );

          setRouteEditPreviewRoute(
            null
          );

          await refreshPlanner({
            silent:
              true,
          });

          await onRouteSaved?.(
            routeEditSession.id
          );
        } catch (
          saveError
        ) {
          console.error(
            "[DriverRoutePlannerMap] route save failed:",
            saveError
          );

          setRouteEditError(
            saveError
              ?.response
              ?.data
              ?.error ||
            "Não foi possível guardar a rota."
          );
        } finally {
          setRouteEditSaving(
            false
          );
        }
      },
      [
        onRouteSaved,
        refreshPlanner,
        routeEditPreviewRoute,
        routeEditSession,
        routeEditViaPoints,
      ]
    );


  // Centrar a sessão selecionada no Simplificado
  // e todas as rotas do motorista no Completo.
  const focusDriverRoute = useCallback(() => {
    const map = mapRef.current;

    if (!map || !mapReady || !selectedDriver) {
      return;
    }

    const bounds = new maplibregl.LngLatBounds();
    let count = 0;

    const add = (lngValue, latValue) => {
      if (lngValue == null || latValue == null) {
        return;
      }

      const lng = Number(lngValue);
      const lat = Number(latValue);

      if (
        !Number.isFinite(lng) ||
        !Number.isFinite(lat) ||
        Math.abs(lng) > 180 ||
        Math.abs(lat) > 90
      ) {
        return;
      }

      bounds.extend([lng, lat]);
      count += 1;
    };

    const rows =
      driverPanelMode === "simple"
        ? panelDriverRows
        : selectedDriverTimelinePoints;

    rows.forEach(({ point }) => {
      add(point?.lng, point?.lat);
    });

    const details =
      driverPanelMode === "simple"
        ? selectedDriverSessionDetails.filter(
            ({ session }) =>
              String(session.id) ===
              String(panelDriverSession?.id)
          )
        : selectedDriverSessionDetails;

    // Incluir a geometria das estradas,
    // não apenas os pontos operacionais.
    const collections = [
      buildSessionRoutesFeatureCollection(
        details,
        routeEditSessionId,
        routeEditPreviewRoute
      ),

      ...(driverPanelMode === "complete"
        ? [
            buildRoutesFeatureCollection(
              selectedDriver,
              selectedConnectionId
            ),
          ]
        : []),
    ];

    collections.forEach((collection) => {
      (collection?.features || []).forEach(
        (feature) => {
          if (
            feature?.geometry?.type !== "LineString"
          ) {
            return;
          }

          (
            feature.geometry.coordinates || []
          ).forEach((coordinate) => {
            add(
              coordinate?.[0],
              coordinate?.[1]
            );
          });
        }
      );
    });

    if (!count) {
      return;
    }

    map.stop();
    map.resize();

    const padding = getDriverMapPadding(map);

    if (count === 1) {
      map.easeTo({
        center: bounds.getCenter().toArray(),

        offset: [
          (padding.left - padding.right) / 2,
          0,
        ],

        zoom: Math.min(
          12,
          Math.max(10, map.getZoom())
        ),

        duration: 450,
      });
    } else {
      map.fitBounds(bounds, {
        padding,
        maxZoom: 12,
        duration: 550,
      });
    }
  }, [
    mapReady,
    selectedDriver,
    driverPanelMode,
    panelDriverRows,
    selectedDriverTimelinePoints,
    selectedDriverSessionDetails,
    panelDriverSession,
    routeEditSessionId,
    routeEditPreviewRoute,
    selectedConnectionId,
  ]);

  const focusDriverRouteRef = useRef(
    focusDriverRoute
  );

  focusDriverRouteRef.current = focusDriverRoute;

  // Reenquadrar automaticamente depois de mudar
  // de modo, selecionar motorista ou redimensionar.
  useEffect(() => {
    if (
      !mapReady ||
      !selectedDriverKey ||
      routeEditMode
    ) {
      return undefined;
    }

    let timer;

    const scheduleFit = () => {
      window.clearTimeout(timer);

      timer = window.setTimeout(() => {
        focusDriverRouteRef.current?.();
      }, 280);
    };

    const root = mapRef.current
      ?.getContainer()
      ?.closest(
        ".live-transports-map.driver-route-planner"
      );

    const observer =
      typeof ResizeObserver !== "undefined"
        ? new ResizeObserver(scheduleFit)
        : null;

    [
      root?.querySelector(
        ".live-transports-map__sidebar"
      ),

      root?.querySelector(
        ".live-transports-map__selection.is-driver-unified"
      ),

      mapRef.current?.getContainer(),
    ]
      .filter(Boolean)
      .forEach((element) => {
        observer?.observe(element);
      });

    window.addEventListener(
      "resize",
      scheduleFit
    );

    scheduleFit();

    return () => {
      window.clearTimeout(timer);
      observer?.disconnect();

      window.removeEventListener(
        "resize",
        scheduleFit
      );
    };
  }, [
    mapReady,
    selectedDriverKey,
    driverPanelMode,
    panelDriverSession?.id,
    isSidebarCollapsed,
    isDriverPanelOpen,
    routeEditMode,
    selectedDriverTimelinePoints.length,
  ]);

  // As transições também respeitam a área visível.
  const focusConnection = useCallback(
    (connection) => {
      const map = mapRef.current;

      if (
        !mapReady ||
        !map ||
        !connection
      ) {
        return;
      }

      const from = connection.fromPoint;
      const to = connection.toPoint;

      if (
        ![from, to].every(
          (point) =>
            point?.lat != null &&
            point?.lng != null &&
            Number.isFinite(Number(point.lat)) &&
            Number.isFinite(Number(point.lng)) &&
            Math.abs(Number(point.lat)) <= 90 &&
            Math.abs(Number(point.lng)) <= 180
        )
      ) {
        return;
      }

      const bounds =
        new maplibregl.LngLatBounds();

      bounds.extend([
        Number(from.lng),
        Number(from.lat),
      ]);

      bounds.extend([
        Number(to.lng),
        Number(to.lat),
      ]);

      map.stop();
      map.resize();

      map.fitBounds(bounds, {
        padding: getDriverMapPadding(map),
        maxZoom: 12,
        duration: 450,
      });
    },
    [mapReady]
  );


  const toggleDriverSelection =
    useCallback(
      (
        driverKey
      ) => {
        const map =
          mapRef.current;

        const camera =
          map
            ? {
                center:
                  map
                    .getCenter()
                    .toArray(),

                zoom:
                  map.getZoom(),

                bearing:
                  map.getBearing(),

                pitch:
                  map.getPitch(),
              }
            : null;

        setSelectedDriverKey(
          (
            current
          ) =>
            current ===
            driverKey
              ? null
              : driverKey
        );

        if (
          map &&
          camera
        ) {
          window.requestAnimationFrame(
            () => {
              try {
                map.stop();

                map.jumpTo(
                  camera
                );
              } catch {}
            }
          );
        }
      },
      []
    );

  const selectedStatus =
    selectedConnection
      ? getConnectionStatus(
          selectedConnection
        )
      : null;

  const selectedMargin =
    selectedConnection
      ? getConnectionMarginSeconds(
          selectedConnection
        )
      : null;

  return (
    <div
      className={[
        "map-view",
        "live-transports-map",
        "driver-route-planner",
        isSidebarCollapsed
          ? "is-sidebar-collapsed"
          : "",
      ]
        .filter(Boolean)
        .join(" ")}
    >
      <aside className="map-view__sidebar live-transports-map__sidebar">
        <div className="live-transports-map__header">
          <div>
            <h2 className="map-view__sidebar-title">
              Rotas dos motoristas
            </h2>

            <span className="driver-route-planner__sidebar-subtitle">
              {scopedDrivers.length} motoristas associados
            </span>

          </div>

          <button
            type="button"
            className="live-transports-map__refresh"
            onClick={() =>
              refreshPlanner({
                silent: true,
              })
            }
            disabled={
              refreshing ||
              loading
            }
            aria-label="Atualizar planeador"
          >
            <RefreshCw
              size={17}
              className={
                refreshing
                  ? "is-spinning"
                  : ""
              }
            />
          </button>
        </div>

        <div
          className="live-map-mode-toggle"
          role="tablist"
          aria-label="Modo do mapa"
        >
          <button
            type="button"
            onClick={
              onShowSessions
            }
          >
            Sessões
          </button>

          <button
            type="button"
            className="is-active"
          >
            Motoristas
          </button>
        </div>

        <label className="live-transports-map__search">
          <Search
            size={17}
            aria-hidden="true"
          />

          <input
            type="search"
            value={
              searchTerm
            }
            onChange={
              (
                event
              ) =>
                setSearchTerm(
                  event.target.value
                )
            }
            placeholder="Pesquisar motoristas..."
          />
        </label>

        {generatedAt && (
          <div className="live-transports-map__updated">
            <Clock3
              size={14}
            />

            <span>
              Atualizado às{" "}
              {formatUpdatedAt(
                generatedAt,
                locale
              )}
            </span>
          </div>
        )}

        {error && (
          <div className="live-transports-map__error">
            <AlertCircle
              size={18}
            />

            <span>
              {error}
            </span>
          </div>
        )}

        {(loading ||
          currentSessionsLoading) &&
        !scopedDrivers.length ? (
          <div className="live-transports-map__loading">
            <RefreshCw
              size={21}
              className="is-spinning"
            />

            <span>
              A carregar motoristas...
            </span>
          </div>
        ) : filteredDrivers.length ? (
          <div className="live-transports-map__list driver-route-planner__list">
            {filteredDrivers.map(
              (
                driver
              ) => {
                const selected =
                  driver.driverKey ===
                  selectedDriverKey;

                const conflicts =
                  (
                    driver.connections ||
                    []
                  ).filter(
                    (
                      connection
                    ) =>
                      getConnectionStatus(
                        connection
                      ) ===
                      "CONFLICT"
                  ).length;

                const driverProgress =
                  driverProgressByKey.get(
                    driver.driverKey
                  ) || {
                    totalPoints:
                      0,

                    finishedPoints:
                      0,

                    percentage:
                      0,
                  };

                return (
                  <article
                    key={
                      driver.driverKey
                    }
                    className={[
                      "driver-route-card",
                      selected
                        ? "is-selected"
                        : "",
                    ]
                      .filter(Boolean)
                      .join(" ")}
                  >
                    <button
                      type="button"
                      className="driver-route-card__main"
                      onClick={() =>
                        toggleDriverSelection(
                          driver.driverKey
                        )
                      }
                    >
                      <div className="driver-route-card__top">
                        <div className="driver-route-card__identity">
                          <span className="driver-route-card__avatar">
                            <User
                              size={16}
                            />
                          </span>

                          <div>
                            <strong>
                              {
                                driver.driverName
                              }
                            </strong>

                            <small>
                              {
                                driver
                                  .currentSessionCount ??
                                driver.sessions
                                  .filter(
                                    (
                                      session
                                    ) =>
                                      !session
                                        ?.isHistoricalAnchor
                                  )
                                  .length
                              }{" "}
                              sessões atuais
                            </small>
                          </div>
                        </div>

                        {conflicts > 0 ? (
                          <span className="driver-route-card__badge is-conflict">
                            {conflicts} conflito
                            {conflicts !==
                            1
                              ? "s"
                              : ""}
                          </span>
                        ) : (
                          <span className="driver-route-card__badge">
                            {
                              driver.connections
                                .length
                            }{" "}
                            transições
                          </span>
                        )}
                      </div>

                      <div
                        style={{
                          display:
                            "grid",
                          gap: "5px",
                          marginTop:
                            "9px",
                        }}
                      >
                        <div
                          style={{
                            display:
                              "flex",
                            alignItems:
                              "center",
                            justifyContent:
                              "space-between",
                            gap: "8px",
                            fontSize:
                              "0.66rem",
                          }}
                        >
                          <span
                            style={{
                              color:
                                "rgba(226, 239, 255, 0.62)",
                              fontWeight:
                                700,
                            }}
                          >
                            Progresso
                          </span>

                          <strong
                            style={{
                              color:
                                driverProgress.percentage ===
                                100
                                  ? "#86efac"
                                  : "#dbeafe",
                              fontSize:
                                "0.66rem",
                              fontWeight:
                                800,
                            }}
                          >
                            {
                              driverProgress.percentage
                            }
                            % ·{" "}
                            {
                              driverProgress.finishedPoints
                            }
                            /
                            {
                              driverProgress.totalPoints
                            }
                          </strong>
                        </div>

                        <div
                          style={{
                            width:
                              "100%",
                            height:
                              "5px",
                            overflow:
                              "hidden",
                            borderRadius:
                              "999px",
                            background:
                              "rgba(148, 163, 184, 0.16)",
                          }}
                        >
                          <span
                            style={{
                              display:
                                "block",
                              width:
                                `${driverProgress.percentage}%`,
                              height:
                                "100%",
                              borderRadius:
                                "999px",
                              background:
                                driverProgress.percentage ===
                                100
                                  ? "#22c55e"
                                  : "#3b82f6",
                              transition:
                                "width 220ms ease",
                            }}
                          />
                        </div>
                      </div>
                    </button>

                    {selected && (
                      <div className="driver-route-card__connections">
                        {selectedDriverSessionDetails.map(
                          ({
                            session,
                            points,
                            pickupCount,
                            deliveryCount,
                            strategicPointCount,
                            fuelCount,
                          }) => (
                            <div
                              key={
                                session.id
                              }
                              className="driver-route-card__available"
                              style={{
                                alignItems:
                                  "stretch",
                                gap: "7px",
                                padding:
                                  "8px 10px",
                                borderRadius:
                                  "14px",
                              }}
                            >
                              <div
                                style={{
                                  display:
                                    "flex",
                                  alignItems:
                                    "center",
                                  justifyContent:
                                    "space-between",
                                  gap: "6px",
                                  minWidth:
                                    0,
                                }}
                              >
                                <strong
                                  style={{
                                    minWidth:
                                      0,
                                    color:
                                      "#8af5c0",
                                    fontSize:
                                      "0.70rem",
                                    fontWeight:
                                      800,
                                    lineHeight:
                                      1.15,
                                    whiteSpace:
                                      "nowrap",
                                    overflow:
                                      "hidden",
                                    textOverflow:
                                      "ellipsis",
                                  }}
                                >
                                  {getSessionLabel(
                                    session
                                      .transportSessionNumber,
                                    session.id
                                  )}
                                  {session.routeName
                                    ? ` · ${session.routeName}`
                                    : ""}
                                </strong>

                                <div
                                  style={{
                                    display:
                                      "flex",
                                    alignItems:
                                      "center",
                                    gap:
                                      "5px",
                                    flex:
                                      "0 0 auto",
                                  }}
                                >
                                  {session
                                    .isHistoricalAnchor && (
                                    <span
                                      style={{
                                        padding:
                                          "3px 6px",
                                        borderRadius:
                                          "999px",
                                        background:
                                          "rgba(34, 197, 94, 0.14)",
                                        color:
                                          "#86efac",
                                        fontSize:
                                          "0.58rem",
                                        fontWeight:
                                          800,
                                        lineHeight:
                                          1,
                                        whiteSpace:
                                          "nowrap",
                                      }}
                                    >
                                      Última concluída
                                    </span>
                                  )}

                                  <span
                                    style={{
                                      color:
                                        "rgba(222, 247, 255, 0.62)",
                                      fontSize:
                                        "0.62rem",
                                      fontWeight:
                                        700,
                                      whiteSpace:
                                        "nowrap",
                                    }}
                                  >
                                    {
                                      points.length
                                    }{" "}
                                    ponto
                                    {points.length !==
                                    1
                                      ? "s"
                                      : ""}
                                  </span>
                                </div>
                              </div>

                              <div
                                style={{
                                  display:
                                    "flex",
                                  alignItems:
                                    "center",
                                  flexWrap:
                                    "wrap",
                                  gap: "4px",
                                }}
                              >
                                {pickupCount >
                                  0 && (
                                  <span
                                    style={{
                                      padding:
                                        "3px 7px",
                                      borderRadius:
                                        "999px",
                                      background:
                                        "rgba(11, 141, 189, 0.15)",
                                      color:
                                        "#a7e8ff",
                                      fontSize:
                                        "0.62rem",
                                      fontWeight:
                                        750,
                                      lineHeight:
                                        1,
                                    }}
                                  >
                                    {pickupCount} carga
                                    {pickupCount !==
                                    1
                                      ? "s"
                                      : ""}
                                  </span>
                                )}

                                {deliveryCount >
                                  0 && (
                                  <span
                                    style={{
                                      padding:
                                        "3px 7px",
                                      borderRadius:
                                        "999px",
                                      background:
                                        "rgba(15, 23, 42, 0.28)",
                                      color:
                                        "#dce7ff",
                                      fontSize:
                                        "0.62rem",
                                      fontWeight:
                                        750,
                                      lineHeight:
                                        1,
                                    }}
                                  >
                                    {deliveryCount} descarga
                                    {deliveryCount !==
                                    1
                                      ? "s"
                                      : ""}
                                  </span>
                                )}

                                {strategicPointCount >
                                  0 && (
                                  <span
                                    style={{
                                      padding:
                                        "3px 7px",
                                      borderRadius:
                                        "999px",
                                      background:
                                        "rgba(147, 51, 234, 0.18)",
                                      color:
                                        "#e6c8ff",
                                      fontSize:
                                        "0.62rem",
                                      fontWeight:
                                        750,
                                      lineHeight:
                                        1,
                                    }}
                                  >
                                    {strategicPointCount} estratégico
                                    {strategicPointCount !==
                                    1
                                      ? "s"
                                      : ""}
                                  </span>
                                )}

                                {fuelCount >
                                  0 && (
                                  <span
                                    style={{
                                      padding:
                                        "3px 7px",
                                      borderRadius:
                                        "999px",
                                      background:
                                        "rgba(245, 158, 11, 0.16)",
                                      color:
                                        "#ffd98f",
                                      fontSize:
                                        "0.62rem",
                                      fontWeight:
                                        750,
                                      lineHeight:
                                        1,
                                    }}
                                  >
                                    {fuelCount} combustível
                                  </span>
                                )}
                              </div>

                              {points.length ? (
                                <div
                                  style={{
                                    display:
                                      "grid",
                                    gap: "0",
                                    borderTop:
                                      "1px solid rgba(148, 163, 184, 0.10)",
                                  }}
                                >
                                  {points.map(
                                    (
                                      point,
                                      index
                                    ) => {
                                      const locationLine =
                                        getPointLocationLine(
                                          point
                                        );

                                      const postalAddressLine =
                                        getPointPostalAddressLine(
                                          point
                                        );

                                      const dateTimeLine =
                                        formatOperationalPointDateTime(
                                          point,
                                          locale
                                        );

                                      const flag =
                                        getPointCountryFlag(
                                          point
                                        );

                                      const statusLabel =
                                        getPointStatusLabel(
                                          point
                                        );

                                      const statusPresentation =
                                        getPointStatusPresentation(
                                          point
                                        );

                                      return (
                                        <div
                                          key={
                                            point.id ||
                                            `${session.id}-${index}`
                                          }
                                          style={{
                                            display:
                                              "grid",
                                            gridTemplateColumns:
                                              "34px minmax(0, 1fr)",
                                            gap: "7px",
                                            alignItems:
                                              "start",
                                            padding:
                                              "8px 0",
                                            borderBottom:
                                              index <
                                              points.length -
                                                1
                                                ? "1px solid rgba(148, 163, 184, 0.10)"
                                                : "0",
                                          }}
                                        >
                                          <span
                                            style={{
                                              minWidth:
                                                "30px",
                                              height:
                                                "22px",
                                              display:
                                                "inline-flex",
                                              alignItems:
                                                "center",
                                              justifyContent:
                                                "center",
                                              borderRadius:
                                                "8px",
                                              background:
                                                getPointSidebarBadgeColor(
                                                  point
                                                ),
                                              color:
                                                "#ffffff",
                                              fontSize:
                                                "0.60rem",
                                              fontWeight:
                                                850,
                                              lineHeight:
                                                1,
                                            }}
                                          >
                                            {getPointSequenceLabel(
                                              point,
                                              index
                                            )}
                                          </span>

                                          <div
                                            style={{
                                              display:
                                                "grid",
                                              gap: "2px",
                                              minWidth:
                                                0,
                                            }}
                                          >
                                            <div
                                              style={{
                                                display:
                                                  "flex",
                                                alignItems:
                                                  "baseline",
                                                gap: "6px",
                                                minWidth:
                                                  0,
                                              }}
                                            >
                                              <strong
                                                style={{
                                                  minWidth:
                                                    0,
                                                  color:
                                                    "#eff8ff",
                                                  fontSize:
                                                    "0.72rem",
                                                  fontWeight:
                                                    800,
                                                  lineHeight:
                                                    1.1,
                                                  whiteSpace:
                                                    "nowrap",
                                                  overflow:
                                                    "hidden",
                                                  textOverflow:
                                                    "ellipsis",
                                                }}
                                              >
                                                {getPointName(
                                                  point
                                                )}
                                              </strong>

                                              <span
                                                style={{
                                                  flex:
                                                    "0 0 auto",
                                                  color:
                                                    getPointSidebarTypeColor(
                                                      point
                                                    ),
                                                  fontSize:
                                                    "0.60rem",
                                                  fontWeight:
                                                    800,
                                                }}
                                              >
                                                {getPointTypeLabel(
                                                  point
                                                )}
                                              </span>
                                            </div>

                                            {locationLine && (
                                              <span
                                                style={{
                                                  display:
                                                    "flex",
                                                  alignItems:
                                                    "center",
                                                  gap: "4px",
                                                  minWidth:
                                                    0,
                                                  color:
                                                    "rgba(226, 239, 255, 0.80)",
                                                  fontSize:
                                                    "0.64rem",
                                                  fontWeight:
                                                    700,
                                                  lineHeight:
                                                    1.2,
                                                }}
                                              >
                                                <span>
                                                  {flag}
                                                </span>

                                                <MapPin
                                                  size={11}
                                                />

                                                <span
                                                  style={{
                                                    minWidth:
                                                      0,
                                                    overflow:
                                                      "hidden",
                                                    textOverflow:
                                                      "ellipsis",
                                                  }}
                                                >
                                                  {
                                                    locationLine
                                                  }
                                                </span>
                                              </span>
                                            )}

                                            {postalAddressLine && (
                                              <span
                                                style={{
                                                  color:
                                                    "rgba(226, 239, 255, 0.57)",
                                                  fontSize:
                                                    "0.62rem",
                                                  lineHeight:
                                                    1.2,
                                                }}
                                              >
                                                {
                                                  postalAddressLine
                                                }
                                              </span>
                                            )}

                                            {dateTimeLine && (
                                              <span
                                                style={{
                                                  display:
                                                    "flex",
                                                  alignItems:
                                                    "center",
                                                  gap: "4px",
                                                  color:
                                                    "rgba(226, 239, 255, 0.57)",
                                                  fontSize:
                                                    "0.62rem",
                                                  lineHeight:
                                                    1.15,
                                                }}
                                              >
                                                <Clock3
                                                  size={10}
                                                />

                                                <span>
                                                  {
                                                    dateTimeLine
                                                  }
                                                </span>
                                              </span>
                                            )}

                                            <span
                                              style={{
                                                display:
                                                  "inline-flex",
                                                alignItems:
                                                  "center",
                                                justifyContent:
                                                  "center",
                                                justifySelf:
                                                  "start",
                                                marginTop:
                                                  "2px",
                                                padding:
                                                  "3px 7px",
                                                borderRadius:
                                                  "999px",
                                                background:
                                                  statusPresentation.background,
                                                color:
                                                  statusPresentation.color,
                                                fontSize:
                                                  "0.58rem",
                                                fontWeight:
                                                  800,
                                                lineHeight:
                                                  1,
                                              }}
                                            >
                                              {
                                                statusLabel
                                              }
                                            </span>
                                          </div>
                                        </div>
                                      );
                                    }
                                  )}
                                </div>
                              ) : (
                                <span
                                  style={{
                                    fontSize:
                                      "0.66rem",
                                    opacity:
                                      0.72,
                                  }}
                                >
                                  Sem pontos operacionais visíveis nesta sessão.
                                </span>
                              )}
                            </div>
                          )
                        )}

                        {driver.connections.length ? (
                          <>
                            <div
                              className="driver-route-card__available"
                              style={{
                                alignItems:
                                  "stretch",
                                gap: "6px",
                              }}
                            >
                              <strong>
                                Transições entre sessões
                              </strong>

                              <span
                                style={{
                                  opacity:
                                    0.82,
                                  fontSize:
                                    "0.78rem",
                                }}
                              >
                                Deslocação do motorista entre o fim de uma sessão e o início da seguinte.
                              </span>
                            </div>

                            {driver.connections.map(
                              (
                                connection
                              ) => {
                                const connectionStatus =
                                  getConnectionStatus(
                                    connection
                                  );

                                const margin =
                                  getConnectionMarginSeconds(
                                    connection
                                  );

                                const active =
                                  String(
                                    connection.id
                                  ) ===
                                  String(
                                    selectedConnectionId
                                  );

                                return (
                                  <button
                                    key={
                                      connection.id
                                    }
                                    type="button"
                                    className={[
                                      "driver-connection-card",
                                      active
                                        ? "is-selected"
                                        : "",
                                      connectionStatus ===
                                      "CONFLICT"
                                        ? "is-conflict"
                                        : "",
                                    ]
                                      .filter(Boolean)
                                      .join(" ")}
                                    onClick={() => {
                                      setSelectedConnectionId(
                                        connection.id
                                      );

                                      focusConnection(
                                        connection
                                      );
                                    }}
                                  >
                                    <div className="driver-connection-card__sessions">
                                      <span>
                                        {getSessionLabel(
                                          connection
                                            .fromTransportSessionNumber,
                                          connection
                                            .fromTransportSessionId
                                        )}
                                      </span>

                                      <Route
                                        size={14}
                                      />

                                      <span>
                                        {getSessionLabel(
                                          connection
                                            .toTransportSessionNumber,
                                          connection
                                            .toTransportSessionId
                                        )}
                                      </span>
                                    </div>

                                    <div className="driver-connection-card__route">
                                      <span>
                                        <MapPin
                                          size={13}
                                        />

                                        {getPointName(
                                          connection.fromPoint
                                        )}
                                      </span>

                                      <span className="driver-connection-card__arrow">
                                        ↓
                                      </span>

                                      <span>
                                        <MapPin
                                          size={13}
                                        />

                                        {getPointName(
                                          connection.toPoint
                                        )}
                                      </span>
                                    </div>

                                    <div className="driver-connection-card__metrics">
                                      <span>
                                        <Truck
                                          size={13}
                                        />

                                        {connection.routing
                                          ? "A calcular..."
                                          : formatKm(
                                              connection
                                                .route
                                                ?.distanceKm
                                            ) ||
                                            "—"}
                                      </span>

                                      <span>
                                        <Clock3
                                          size={13}
                                        />

                                        {connection.routing
                                          ? "..."
                                          : formatDuration(
                                              connection
                                                .route
                                                ?.durationSeconds
                                            ) ||
                                            "—"}
                                      </span>
                                    </div>

                                    {Number.isFinite(
                                      Number(
                                        connection.availableSeconds
                                      )
                                    ) && (
                                      <div className="driver-connection-card__availability">
                                        <span>
                                          Janela disponível
                                        </span>

                                        <strong>
                                          {formatDuration(
                                            connection.availableSeconds
                                          )}
                                        </strong>
                                      </div>
                                    )}

                                    {connectionStatus ===
                                      "CONFLICT" && (
                                      <div className="driver-connection-card__warning">
                                        <AlertCircle
                                          size={14}
                                        />

                                        <span>
                                          Ligação potencialmente impossível
                                          {Number.isFinite(
                                            margin
                                          )
                                            ? ` · faltam ${formatDuration(
                                                Math.abs(
                                                  margin
                                                )
                                              )}`
                                            : ""}
                                        </span>
                                      </div>
                                    )}
                                  </button>
                                );
                              }
                            )}
                          </>
                        ) : (
                          <div className="driver-route-card__available">
                            Sem próxima sessão atribuída
                          </div>
                        )}
                      </div>
                    )}
                  </article>
                );
              }
            )}
          </div>
        ) : (
          <div className="map-view__sidebar-empty">
            <div className="map-view__sidebar-empty-icon">
              <User />
            </div>

            <strong>
              Nenhum motorista encontrado
            </strong>

            <span>
              Os motoristas com sessões atribuídas aparecerão aqui automaticamente.
            </span>
          </div>
        )}
      </aside>

      <button
        type="button"
        className="map-view__sidebar-toggle"
        onClick={() =>
          setIsSidebarCollapsed(
            (
              current
            ) =>
              !current
          )
        }
        aria-label={
          isSidebarCollapsed
            ? "Mostrar painel"
            : "Ocultar painel"
        }
      >
        {isSidebarCollapsed ? (
          <ChevronRight
            size={18}
          />
        ) : (
          <ChevronLeft
            size={18}
          />
        )}
      </button>

      <section className="map-view__canvas">
        <div className="map-base live-transports-map__map">
          <div
            ref={
              mapContainerRef
            }
            className="map-base__canvas"
          />

          <div
            className={[
              "driver-route-planner__route-editor",
              routeEditMode
                ? "is-active"
                : "",
            ]
              .filter(Boolean)
              .join(" ")}
          >
            {!routeEditMode ? (
              <button
                type="button"
                className="driver-route-planner__route-edit-button"
                onClick={() => {
                  clearRouteEditPreviewRequest();

                  setRouteEditMode(
                    true
                  );

                  setRouteEditSessionId(
                    null
                  );

                  setRouteEditViaPoints(
                    []
                  );

                  setRouteEditPreviewRoute(
                    null
                  );

                  setRouteEditError(
                    null
                  );
                }}
              >
                <Route
                  size={15}
                />

                Editar rota
              </button>
            ) : (
              <>
                <div className="driver-route-planner__route-edit-status">
                  <Route
                    size={15}
                  />

                  <span>
                    {routeEditSession
                      ? `TS ${
                          routeEditSession
                            .transportSessionNumber ??
                          routeEditSession
                            .id
                        }`
                      : "Arrasta uma rota azul"}
                  </span>
                </div>

                <span className="driver-route-planner__route-edit-hint">
                  {routeEditSession
                    ? "Puxa a linha azul para alterar o trajeto. Larga para criar o ponto roxo."
                    : "Puxa uma rota azul para começar a editar."}
                </span>

                <div className="driver-route-planner__route-edit-actions">
                  {routeEditSession && (
                    <button
                      type="button"
                      className="is-reset"
                      disabled={
                        routeEditSaving
                      }
                      onClick={() => {
                        setRouteEditViaPoints(
                          getSavedSessionViaPoints(
                            routeEditSession
                              .routeSnapshot
                          )
                        );

                        setRouteEditError(
                          null
                        );
                      }}
                    >
                      Repor
                    </button>
                  )}

                  <button
                    type="button"
                    disabled={
                      routeEditSaving
                    }
                    onClick={() => {
                      clearRouteEditPreviewRequest();

                      setRouteEditMode(
                        false
                      );

                      setRouteEditSessionId(
                        null
                      );

                      setRouteEditViaPoints(
                        []
                      );

                      setRouteEditPreviewRoute(
                        null
                      );

                      setRouteEditError(
                        null
                      );
                    }}
                  >
                    Cancelar
                  </button>

                  <button
                    type="button"
                    className="is-save"
                    disabled={
                      routeEditSaving ||
                      !routeEditSession ||
                      !routeEditPreviewRoute
                    }
                    onClick={
                      handleSaveRouteEdit
                    }
                  >
                    {routeEditSaving
                      ? "A guardar..."
                      : "Guardar rota"}
                  </button>
                </div>

                {routeEditError && (
                  <div className="driver-route-planner__route-edit-error">
                    {
                      routeEditError
                    }
                  </div>
                )}
              </>
            )}
          </div>

          {(loading ||
            currentSessionsLoading) &&
            !scopedDrivers.length && (
              <div className="live-transports-map__map-loading">
                <RefreshCw
                  size={24}
                  className="is-spinning"
                />

                <strong>
                  A preparar o planeador...
                </strong>
              </div>
            )}


          {selectedDriver && isDriverPanelOpen && (
            renderDriverPanel ? renderDriverPanel({
              driver: selectedDriver,

              sessions: panelDriverSessions,
              activeSession: panelDriverSession,

              simpleRows: panelDriverRows,
              allRows: selectedDriverTimelinePoints,

              mode: driverPanelMode,
              onModeChange: setDriverPanelMode,
              onSelectSession: setDriverPanelSessionId,
              onClose: () => setIsDriverPanelOpen(false),

              progress: driverProgressByKey.get(
                selectedDriver.driverKey
              ),

              getPointStatusLabel,


              onFocusPoint: (point) => {
                const map = mapRef.current;

                if (
                  !map ||
                  point?.lat == null ||
                  point?.lng == null
                ) {
                  return;
                }

                const lat = Number(point.lat);
                const lng = Number(point.lng);

                if (
                  !Number.isFinite(lat) ||
                  !Number.isFinite(lng) ||
                  Math.abs(lat) > 90 ||
                  Math.abs(lng) > 180
                ) {
                  return;
                }

                map.stop();
                map.resize();

                const padding = getDriverMapPadding(map);

                map.easeTo({
                  center: [lng, lat],

                  offset: [
                    (padding.left - padding.right) / 2,
                    0,
                  ],

                  zoom: Math.max(10, map.getZoom()),
                  duration: 450,
                });
              },

              onFocusRoute: focusDriverRoute,


              connections: selectedDriver.connections.map(
                (connection) => ({
                  ...connection,

                  isConflict:
                    getConnectionStatus(connection) ===
                    "CONFLICT",

                  fromLabel: getSessionLabel(
                    connection.fromTransportSessionNumber,
                    connection.fromTransportSessionId
                  ),

                  toLabel: getSessionLabel(
                    connection.toTransportSessionNumber,
                    connection.toTransportSessionId
                  ),

                  kmLabel:
                    formatKm(connection.route?.distanceKm) ||
                    "—",

                  durationLabel:
                    formatDuration(
                      connection.route?.durationSeconds
                    ) ||
                    (connection.routing
                      ? "A calcular..."
                      : "—"),
                })
              ),

              selectedConnection: selectedConnection
                ? {
                    ...selectedConnection,

                    fromLabel: getSessionLabel(
                      selectedConnection
                        .fromTransportSessionNumber,
                      selectedConnection
                        .fromTransportSessionId
                    ),

                    toLabel: getSessionLabel(
                      selectedConnection
                        .toTransportSessionNumber,
                      selectedConnection
                        .toTransportSessionId
                    ),

                    isConflict:
                      getConnectionStatus(
                        selectedConnection
                      ) === "CONFLICT",

                    marginLabel:
                      Number.isFinite(selectedMargin)
                        ? `${
                            selectedMargin < 0
                              ? "−"
                              : "+"
                          }${formatDuration(
                            Math.abs(selectedMargin)
                          )}`
                        : null,
                  }
                : null,

              onSelectConnection: (connection) => {
                setSelectedConnectionId(connection.id);
                focusConnection(connection);
              },
            }) : (
              <div
                className={[
                  "live-transports-map__selection",
                  "driver-route-planner__selection",
                  driverPanelMode === "simple"
                    ? "is-driver-simple"
                    : "is-driver-complete",
                ].join(" ")}
                style={{
                  "--session-panel-width":
                    driverPanelMode === "simple"
                      ? "500px"
                      : "835px",
                }}
              >

              <SharedPanelHeader
                icon={<User size={27} strokeWidth={2.1} />}
                title={`Rota do motorista ${selectedDriver.driverName}`}
                status={`${
                  selectedDriver.currentSessionCount ??
                  selectedDriver.sessions.filter(
                    (session) => !session.isHistoricalAnchor
                  ).length
                } sessões`}
                statusClassName="driver-route-planner__session-count"
                mode={driverPanelMode}
                onModeChange={setDriverPanelMode}
                onClose={() => setIsDriverPanelOpen(false)}
                closeLabel="Fechar detalhes do motorista"
              >
                {driverPanelMode === "simple" && (
                  <OperationalSessionTabs
                    sessions={panelDriverSessions}
                    selectedId={panelDriverSession?.id}
                    onSelect={setDriverPanelSessionId}
                    getPoints={getSessionOperationalPoints}
                    getLabel={(session) =>
                      getSessionLabel(
                        session.transportSessionNumber,
                        session.id
                      )
                    }
                    getFlag={getPointCountryFlagImage}
                    getName={getPointName}
                  />
                )}
              </SharedPanelHeader>

              <div className="driver-route-planner__route-summary">
                <div className="live-transports-map__selection-route">
                  <span>
                    {getPointCountryFlagImage(
                      visibleDriverRows[0]?.point
                    )}{" "}
                    {visibleDriverRows[0]
                      ? getPointName(visibleDriverRows[0].point)
                      : "Origem não definida"}
                  </span>

                  <span className="live-transports-map__selection-arrow">
                    →
                  </span>

                  <span>
                    {getPointCountryFlagImage(
                      visibleDriverRows[
                        visibleDriverRows.length - 1
                      ]?.point
                    )}{" "}
                    {visibleDriverRows.length
                      ? getPointName(
                          visibleDriverRows[
                            visibleDriverRows.length - 1
                          ].point
                        )
                      : "Destino não definido"}
                  </span>
                </div>

                {driverPanelMode === "simple" && (
                  <div className="live-transports-map__selection-simple-metrics">
                    <span title="Distância da sessão">
                      <Route size={17} aria-hidden="true" />
                      <strong>
                        {panelDriverSession?.routeTotals?.km != null &&
                        Number.isFinite(
                          Number(panelDriverSession.routeTotals.km)
                        )
                          ? `${Math.round(
                              Number(panelDriverSession.routeTotals.km)
                            )} km`
                          : "— km"}
                      </strong>
                    </span>

                    <span title="Condução prevista da sessão">
                      <Clock3 size={17} aria-hidden="true" />
                      <strong>
                        {panelDriverSession?.routeTotals?.minutes != null &&
                        Number.isFinite(
                          Number(panelDriverSession.routeTotals.minutes)
                        )
                          ? formatDuration(
                              Number(panelDriverSession.routeTotals.minutes) * 60
                            )
                          : "—"}
                      </strong>
                    </span>
                  </div>
                )}


                <div className="live-transports-map__selection-progress">
                  <div>
                    <span>Progresso operacional</span>

                    <strong>
                      {driverPanelMode === "simple"
                        ? panelPercent
                        : (
                            driverProgressByKey.get(
                              selectedDriver.driverKey
                            )?.percentage ?? 0
                          )}%
                    </strong>
                  </div>

                  <div className="live-transports-map__selection-progress-track">
                    <span
                      style={{
                        display: "block",

                        width: `${
                          driverPanelMode === "simple"
                            ? panelPercent
                            : (
                                driverProgressByKey.get(
                                  selectedDriver.driverKey
                                )?.percentage ?? 0
                              )
                        }%`,

                        height: "100%",
                        background: "#2699ff",
                        borderRadius: "inherit",
                      }}
                    />
                  </div>

                  <small>
                    {driverPanelMode === "simple"
                      ? panelCompleted
                      : (
                          driverProgressByKey.get(
                            selectedDriver.driverKey
                          )?.finishedPoints ?? 0
                        )}{" "}
                    de{" "}
                    {driverPanelMode === "simple"
                      ? panelDriverRows.length
                      : (
                          driverProgressByKey.get(
                            selectedDriver.driverKey
                          )?.totalPoints ?? 0
                        )}{" "}
                    pontos
                  </small>
                </div>

              </div>

              <div className="driver-route-planner__selection-body">
                {driverPanelMode === "complete" && (

                  <div className="driver-route-planner__metric-grid">
                    {[
                      {
                        label: "Sessões atuais",
                        value: selectedDriver.currentSessionCount ?? 0,
                      },
                      {
                        label: "Transições",
                        value: selectedDriver.connections.length,
                      },
                      {
                        label: "Conflitos",
                        value: selectedDriver.connections.filter(
                          (connection) =>
                            getConnectionStatus(connection) === "CONFLICT"
                        ).length,
                      },
                      {
                        label: "Pontos atribuídos",
                        value: selectedDriverTimelinePoints.length,
                      },
                      {
                        label: "Concluídos",
                        value: selectedDriverTimelinePoints.filter(
                          ({ point }) => isPointFinished(point)
                        ).length,
                      },
                      {
                        label: "Por concluir",
                        value: selectedDriverTimelinePoints.filter(
                          ({ point }) => !isPointFinished(point)
                        ).length,
                      },
                      {
                        label: "Ligações calculadas",
                        value: selectedDriver.connections.filter(
                          (connection) => Boolean(connection.route?.geometry)
                        ).length,
                      },
                      {
                        label: "Por calcular",
                        value: selectedDriver.connections.filter(
                          (connection) =>
                            connection.routable &&
                            !connection.route?.geometry
                        ).length,
                      },
                    ].map(({ label, value }) => (
                      <div key={label}>
                        <small>{label}</small>
                        <strong>{value}</strong>
                      </div>
                    ))}
                  </div>

                )}

                <div className="driver-route-planner__detail-heading">
                  Sessões atribuídas
                </div>

                <div className="driver-route-planner__assigned-sessions">
                  {selectedDriverSessions.map((session) => {
                    const points = getSessionOperationalPoints(session);
                    const first = points[0];
                    const last = points[points.length - 1];

                    return (
                      <div
                        key={session.id}
                        className={
                          session.isHistoricalAnchor ? "is-historical" : ""
                        }
                      >
                        <strong>
                          {getSessionLabel(
                            session.transportSessionNumber,
                            session.id
                          )}
                        </strong>

                        <span>
                          {getPointCountryFlag(first)} {first
                            ? getPointName(first) : "Origem"} →{" "}
                          {getPointCountryFlag(last)} {last
                            ? getPointName(last) : "Destino"}
                        </span>

                        {session.isHistoricalAnchor && (
                          <small>Concluída</small>
                        )}
                      </div>
                    );
                  })}
                </div>

                <div className="driver-route-planner__detail-heading">
                  Pontos do motorista
                </div>

                <div
                  className={
                    driverPanelMode === "simple"
                      ? "live-transports-map__selection-simple-stops"
                      : "driver-route-planner__stop-list"
                  }
                >
                  {driverPanelMode === "simple"
                    ? panelDriverRows.map(
                        ({ session, point }, index) => {
                          const schedule =
                            formatOperationalPointDateTime(
                              point,
                              locale
                            );

                          const [
                            plannedDate = "",
                            plannedTime = "",
                          ] = schedule.split(" · ");

                          return (
                            <div
                              key={`${session.id}:${point.id ?? index}`}
                              className="live-transports-map__selection-simple-row"
                            >
                              <OperationalSimpleStop
                                number={index + 1}
                                completed={isPointCompleted(point)}
                                type={point?.type}
                                name={
                                  point?.companyName ||
                                  getPointName(point)
                                }
                                flag={getPointCountryFlagImage(point)}
                                postalCode={
                                  point?.postalCode ||
                                  point?.postcode ||
                                  ""
                                }
                                plannedDate={plannedDate}
                                plannedTime={plannedTime}
                                eta={
                                  point?.forecast?.estimatedArrivalAt
                                    ? formatDateTime(
                                        point.forecast.estimatedArrivalAt,
                                        locale
                                      )
                                    : null
                                }
                                onClick={() => {
                                  const lat = Number(point?.lat);
                                  const lng = Number(point?.lng);
                                  const map = mapRef.current;

                                  if (
                                    !map ||
                                    !Number.isFinite(lat) ||
                                    !Number.isFinite(lng) ||
                                    Math.abs(lat) > 90 ||
                                    Math.abs(lng) > 180
                                  ) {
                                    return;
                                  }

                                  map.stop();

                                  map.easeTo({
                                    center: [lng, lat],
                                    zoom: Math.max(
                                      10,
                                      map.getZoom()
                                    ),
                                    duration: 450,
                                  });
                                }}
                              />
                            </div>
                          );
                        }
                      )
                    : selectedDriverTimelinePoints.map(
                        ({ session, point }, index) => (
                          <div
                            key={`${session.id}:${point.id ?? index}`}
                            className="driver-route-planner__stop-row"
                          >
                            <span
                              className={
                                isPointCompleted(point)
                                  ? "is-completed"
                                  : ""
                              }
                            >
                              {isPointCompleted(point)
                                ? "✓"
                                : index + 1}
                            </span>

                            <div>
                              <strong>
                                {getPointCountryFlagImage(point)}
                                {" "}
                                {getPointName(point)}
                              </strong>

                              <small>
                                {getSessionLabel(
                                  session.transportSessionNumber,
                                  session.id
                                )}
                                {" · "}
                                {getPointTypeLabel(point)}

                                {formatOperationalPointDateTime(
                                  point,
                                  locale
                                )
                                  ? ` · ${formatOperationalPointDateTime(
                                      point,
                                      locale
                                    )}`
                                  : ""}
                              </small>
                            </div>

                            <em>
                              {getPointStatusLabel(point)}
                            </em>
                          </div>
                        )
                      )}
                </div>


                {selectedDriver.connections.length > 0 && (
                  <>
                    <div className="driver-route-planner__detail-heading">
                      Transições entre sessões
                    </div>

                    <div className="driver-route-planner__connection-list">
                      {selectedDriver.connections.map((connection) => {
                        const active =
                          String(connection.id) ===
                          String(selectedConnectionId);

                        const conflict =
                          getConnectionStatus(connection) === "CONFLICT";

                        return (
                          <button
                            type="button"
                            key={connection.id}
                            className={[
                              active ? "is-active" : "",
                              conflict ? "is-conflict" : "",
                            ].filter(Boolean).join(" ")}
                            onClick={() => {
                              setSelectedConnectionId(connection.id);
                              focusConnection(connection);
                            }}
                          >
                            <strong>
                              {getSessionLabel(
                                connection.fromTransportSessionNumber,
                                connection.fromTransportSessionId
                              )} → {getSessionLabel(
                                connection.toTransportSessionNumber,
                                connection.toTransportSessionId
                              )}
                            </strong>

                            <span>
                              {formatKm(connection.route?.distanceKm) || "—"}
                              {" · "}
                              {formatDuration(
                                connection.route?.durationSeconds
                              ) || (
                                connection.routing
                                  ? "A calcular..."
                                  : "—"
                              )}
                            </span>

                            {conflict && <em>Conflito</em>}
                          </button>
                        );
                      })}
                    </div>

                    {selectedConnection && (
                      <div className="driver-route-planner__connection-detail">
                        <strong>
                          {getPointName(selectedConnection.fromPoint)} →{" "}
                          {getPointName(selectedConnection.toPoint)}
                        </strong>

                        <span>
                          {selectedStatus === "CONFLICT"
                            ? "Conflito de horários"
                            : selectedStatus === "OK"
                              ? "Ligação viável"
                              : "A calcular ligação"}
                        </span>

                        {Number.isFinite(selectedMargin) && (
                          <small>
                            Margem: {selectedMargin < 0 ? "−" : "+"}
                            {formatDuration(Math.abs(selectedMargin))}
                          </small>
                        )}
                      </div>
                    )}
                  </>
                )}
              </div>

              <button
                type="button"
                className="live-transports-map__selection-focus"
                onClick={() => {
                  const map = mapRef.current;
                  if (!map) return;

                  const bounds = new maplibregl.LngLatBounds();
                  let valid = 0;

                  selectedDriverTimelinePoints.forEach(({ point }) => {
                    const lat = Number(point?.lat);
                    const lng = Number(point?.lng);

                    if (
                      !Number.isFinite(lat) ||
                      !Number.isFinite(lng) ||
                      Math.abs(lat) > 90 ||
                      Math.abs(lng) > 180
                    ) {
                      return;
                    }

                    bounds.extend([lng, lat]);
                    valid += 1;
                  });

                  if (!valid) return;

                  map.stop();

                  if (valid === 1) {
                    map.easeTo({
                      center: bounds.getCenter().toArray(),
                      zoom: Math.max(10, map.getZoom()),
                      duration: 500,
                    });
                  } else {
                    const width = map.getContainer().clientWidth;

                    map.fitBounds(bounds, {
                      padding: {
                        top: 85,
                        bottom: 85,
                        left: isSidebarCollapsed
                          ? 70
                          : Math.min(320, width * 0.27),
                        right: Math.min(
                          driverPanelMode === "simple" ? 360 : 580,
                          width * 0.38
                        ),
                      },
                      maxZoom: 12,
                      duration: 600,
                    });
                  }
                }}
              >

                <Route size={16} />
                Centrar rota no mapa
                <ChevronRight size={16} />
              </button>
            </div>
            )
          )}

        </div>
      </section>
    </div>
  );
};

export default DriverRoutePlannerMap;
